import { Fragment, useEffect, useState } from 'react';
import {
  collection, onSnapshot, addDoc, updateDoc, deleteDoc, getDocs, writeBatch,
  doc, orderBy, query, serverTimestamp, getDoc, setDoc,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { getWeekStart } from '../../utils/exchangeRates';
import StatusPipelineSettings from '../../components/StatusPipelineSettings';
import ContractSettings from '../../components/ContractSettings';

const LETTER_TRIGGERS = [
  { value: 'deposit_paid',        label: 'Deposit paid' },
  { value: 'itinerary_confirmed', label: 'Itinerary confirmed (suppliers notified)' },
  { value: 'tour_live',           label: 'Tour goes live' },
  { value: 'departure_minus_14',  label: '14 days before departure' },
  { value: 'departure_minus_7',   label: '7 days before departure' },
  { value: 'departure_minus_2',   label: '2 days before departure' },
  { value: 'manual',              label: 'Manual only' },
  // CRM pipeline (British Clubs & Schools) — fires when a CRM-origin tour
  // enters the matching stage, under the matching flow.
  { value: 'crm_tour_request_cold_1',       label: 'CRM — Tour request, Cold, email 1 (immediate)' },
  { value: 'crm_tour_request_cold_2',       label: 'CRM — Tour request, Cold, email 2 (+5 days)' },
  { value: 'crm_tour_request_cold_3',       label: 'CRM — Tour request, Cold, email 3 (+5 more days)' },
  { value: 'crm_tour_request_web_quote_1',  label: 'CRM — Tour request, Web Quote, email 1 (immediate)' },
  { value: 'crm_tour_request_web_quote_2',  label: 'CRM — Tour request, Web Quote, email 2 (+5 days)' },
  { value: 'crm_tour_request_web_quote_3',  label: 'CRM — Tour request, Web Quote, email 3 (+5 more days)' },
  { value: 'crm_sent_initial_quote_cold',      label: 'CRM — Sent initial quote, Cold' },
  { value: 'crm_sent_initial_quote_returner',  label: 'CRM — Sent initial quote, Returner' },
  { value: 'crm_sent_initial_quote_referral',  label: 'CRM — Sent initial quote, Referral' },
  { value: 'crm_sent_initial_quote_web_quote', label: 'CRM — Sent initial quote, Web Quote' },
  { value: 'crm_accepted_initial_quote_cold',      label: 'CRM — Accepted initial quote, Cold' },
  { value: 'crm_accepted_initial_quote_returner',  label: 'CRM — Accepted initial quote, Returner' },
  { value: 'crm_accepted_initial_quote_referral',  label: 'CRM — Accepted initial quote, Referral' },
  { value: 'crm_accepted_initial_quote_web_quote', label: 'CRM — Accepted initial quote, Web Quote' },
  { value: 'crm_sent_confirmed_quote_cold',        label: 'CRM — Sent confirmed quote, Cold' },
  { value: 'crm_sent_confirmed_quote_returner',    label: 'CRM — Sent confirmed quote, Returner' },
  { value: 'crm_sent_confirmed_quote_referral',    label: 'CRM — Sent confirmed quote, Referral' },
  { value: 'crm_sent_confirmed_quote_web_quote',   label: 'CRM — Sent confirmed quote, Web Quote' },
  { value: 'crm_accepted_cold',      label: 'CRM — Accepted, Cold' },
  { value: 'crm_accepted_returner',  label: 'CRM — Accepted, Returner' },
  { value: 'crm_accepted_referral',  label: 'CRM — Accepted, Referral' },
  { value: 'crm_accepted_web_quote', label: 'CRM — Accepted, Web Quote' },
];

const MERGE_TAGS = [
  { tag: '{{contact_name}}',     desc: 'Tour contact name' },
  { tag: '{{destination}}',      desc: 'Destination' },
  { tag: '{{sport}}',            desc: 'Sport' },
  { tag: '{{departure_date}}',   desc: 'Departure date (dd/mm/yyyy)' },
  { tag: '{{return_date}}',      desc: 'Return date (dd/mm/yyyy)' },
  { tag: '{{participants}}',     desc: 'Number of participants' },
  { tag: '{{tour_leader_name}}', desc: 'Tour leader name' },
  { tag: '{{group_type}}',       desc: 'Group type' },
  { tag: '{{portal_url}}',       desc: 'Customer portal link' },
  { tag: '{{company_name}}',     desc: 'Brand name ("MST Sports Tours" or "Your Tour")' },
  { tag: '{{nights}}',           desc: 'Number of nights' },
  { tag: '{{price}}',            desc: 'Confirmed quote total, with currency symbol' },
  { tag: '{{deposit_amount}}',   desc: 'Deposit due (£100 per traveller), with currency symbol' },
  { tag: '{{deposit_due_date}}', desc: 'Deposit due date — 14 days from when the email is sent' },
  { tag: '{{brochure_link}}',    desc: 'Link to the Spain brochure' },
  { tag: '{{website_url}}',      desc: 'Brand website — mstsportstours.com or your-tour.co.uk' },
];

const DEFAULT_SPORTS = [
  'Football', 'Rugby', 'Cricket', 'Tennis', 'Golf', 'Netball',
  'Basketball', 'Athletics', 'Swimming', 'Cycling',
];

const DEFAULT_GROUP_TYPES = [
  'Football club',
  'School Football team',
  'Multisport college',
  'LLS',
  'Padelante',
  'NSG',
  'MIC Cup',
  'Your Tour – Football Club',
  'Your Tour – School Team',
];

const DEFAULT_CHECKLIST_TASKS = [
  'Confirm group size and participant list',
  'Book flights and confirm itinerary',
  'Book accommodation',
  'Arrange ground transport',
  'Confirm fixtures / match schedule',
  'Purchase match tickets',
  'Arrange training sessions',
  'Organise stadium tour',
  'Prepare travel documents and visas',
  'Send pre-departure pack to group leader',
  'Brief MST staff on tour programme',
  'Collect deposit payment',
  'Collect final balance',
  'Post-tour debrief',
];

function labelToKey(label) {
  return label
    .trim()
    .replace(/[^a-zA-Z0-9\s]/g, '')
    .split(/\s+/)
    .map((w, i) =>
      i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
    )
    .join('');
}

const DEFAULT_PARTICIPANT_FIELDS = [
  { label: 'First name',                       key: 'firstName',             type: 'text', required: true,  placeholder: '',                                        fullWidth: false, order: 0 },
  { label: 'Last name',                        key: 'lastName',              type: 'text', required: true,  placeholder: '',                                        fullWidth: false, order: 1 },
  { label: 'Date of birth',                    key: 'dateOfBirth',           type: 'date', required: true,  placeholder: '',                                        fullWidth: false, order: 2 },
  { label: 'Passport / ID number',             key: 'passportNumber',        type: 'text', required: false, placeholder: 'Optional',                                fullWidth: false, order: 3 },
  { label: 'Dietary requirements / allergies', key: 'dietaryRequirements',   type: 'text', required: false, placeholder: 'Optional — e.g. Vegetarian, Nut allergy', fullWidth: true,  order: 4 },
  { label: 'Emergency contact name',           key: 'emergencyContactName',  type: 'text', required: false, placeholder: 'Optional',                                fullWidth: false, order: 5 },
  { label: 'Emergency contact phone',          key: 'emergencyContactPhone', type: 'tel',  required: false, placeholder: 'Optional',                                fullWidth: false, order: 6 },
];

const PF_TYPE_LABELS = {
  text: 'Short text', textarea: 'Long text', date: 'Date', tel: 'Phone', email: 'Email', number: 'Number',
};

const DEFAULT_TOUR_CATEGORIES = [
  { name: 'Hotel & Food',      order: 0 },
  { name: 'Flights',           order: 1 },
  { name: 'Transport',         order: 2 },
  { name: 'Match Tickets',     order: 3 },
  { name: 'Stadium Tour',      order: 4 },
  { name: 'Fixtures',          order: 5 },
  { name: 'Training Sessions', order: 6 },
  { name: 'Padel',             order: 7 },
  { name: 'Yoga',              order: 8 },
  { name: 'Waterpark',         order: 9 },
];

const DEFAULT_SUPPLIER_CATEGORIES = [
  { name: 'Hotel',             order: 0 },
  { name: 'Flights',           order: 1 },
  { name: 'Airport Transfer',  order: 2 },
  { name: 'Coach Transfer',    order: 3 },
  { name: 'Match Arrangement', order: 4 },
  { name: 'Coaching',          order: 5 },
  { name: 'Equipment',         order: 6 },
  { name: 'Catering',          order: 7 },
  { name: 'Other',             order: 8 },
];

const DEFAULT_EXPENSE_CATEGORIES = [
  { name: 'Accommodation',            order: 0 },
  { name: 'Flights',                  order: 1 },
  { name: 'Ground Transport',         order: 2 },
  { name: 'Match Tickets',            order: 3 },
  { name: 'Stadium Tour',             order: 4 },
  { name: 'Training Facilities',      order: 5 },
  { name: 'Meals & Catering',         order: 6 },
  { name: 'Marketing',                order: 7 },
  { name: 'Staff Costs',              order: 8 },
  { name: 'Professional Services',    order: 9 },
  { name: 'Software & Subscriptions', order: 10 },
  { name: 'Other',                    order: 11 },
];

const GROUPS = [
  { key: 'tour-setup',      label: 'Tour Setup' },
  { key: 'operations',      label: 'Operations' },
  { key: 'suppliers',       label: 'Suppliers' },
  { key: 'communications',  label: 'Communications' },
  { key: 'finance',         label: 'Finance' },
];

const SECTIONS_META = [
  { key: 'tour-categories',       label: 'Tour Categories',             group: 'tour-setup',     icon: '🗂️',  desc: 'Manage itinerary line-item categories' },
  { key: 'quote-tool',            label: 'Quote Tool',                 group: 'tour-setup',     icon: '✈️',  desc: 'Public instant quote — ground price, transport, extras & flight data' },
  { key: 'group-types',           label: 'Group Types',                 group: 'tour-setup',     icon: '👥',  desc: 'Customer group type options' },
  { key: 'sports',                label: 'Sports',                      group: 'tour-setup',     icon: '⚽',  desc: 'Sport options for tours' },
  { key: 'participant-fields',    label: 'Participant Fields',          group: 'tour-setup',     icon: '📋',  desc: 'Registration form fields' },
  { key: 'status-pipeline',       label: 'Status Pipeline',            group: 'operations',     icon: '🚦',  desc: 'Checklist items under each pipeline stage, per portal', adminOnly: true },
  { key: 'checklist-templates',   label: 'Checklist Templates',        group: 'operations',     icon: '✅',  desc: 'Default ops checklist tasks' },
  { key: 'itinerary-palette',     label: 'Itinerary Palette',          group: 'operations',     icon: '🎨',  desc: 'Skeletal itinerary item palette' },
  { key: 'supplier-categories',   label: 'Supplier Categories',        group: 'suppliers',      icon: '🏢',  desc: 'Service categories and fields' },
  { key: 'supplier-emails',       label: 'Supplier Emails',            group: 'suppliers',      icon: '📧',  desc: 'Automated supplier email toggle' },
  { key: 'status-emails',         label: 'Status Update Emails',       group: 'communications', icon: '🔕',  desc: 'Customer status notices and staff status alerts' },
  { key: 'letter-templates',      label: 'Letter Templates',           group: 'communications', icon: '✉️',  desc: 'Customer letter templates and triggers' },
  { key: 'contract-signing',      label: 'Contract Signing',           group: 'communications', icon: '✍️',  desc: 'Tour contract e-signing switch and MST countersignature', adminOnly: true },
  { key: 'expense-categories',    label: 'Expense Categories',         group: 'finance',        icon: '🧾',  desc: 'Categories for expenses and receipt scanning' },
  { key: 'exchange-rates',        label: 'Exchange Rates',             group: 'finance',        icon: '💱',  desc: 'EUR and USD rate overrides' },
  { key: 'finance-contact',       label: 'Finance Contact',            group: 'finance',        icon: '👤',  desc: 'Your Tour finance contact details' },
];

export default function AdminSettings() {
  const { currentUser, isAdmin } = useAuth();
  const visibleSections = SECTIONS_META.filter((s) => !s.adminOnly || isAdmin);

  // Tour categories
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);
  const [showInput, setShowInput] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  // Expense categories
  const [expenseCats, setExpenseCats] = useState([]);
  const [expenseCatsLoading, setExpenseCatsLoading] = useState(true);
  const [expenseCatsSeeding, setExpenseCatsSeeding] = useState(false);
  const [newExpenseCat, setNewExpenseCat] = useState('');
  const [addingExpenseCat, setAddingExpenseCat] = useState(false);
  const [showExpenseCatInput, setShowExpenseCatInput] = useState(false);
  const [editingExpenseCatId, setEditingExpenseCatId] = useState(null);
  const [editExpenseCatName, setEditExpenseCatName] = useState('');

  // Sports
  const [sports, setSports] = useState([]);
  const [sportsLoading, setSportsLoading] = useState(true);
  const [sportsSeeding, setSportsSeeding] = useState(false);
  const [newSport, setNewSport] = useState('');
  const [addingSport, setAddingSport] = useState(false);
  const [showSportInput, setShowSportInput] = useState(false);
  const [editingSportId, setEditingSportId] = useState(null);
  const [editSportName, setEditSportName] = useState('');

  // Group types
  const [groupTypes, setGroupTypes] = useState([]);
  const [groupTypesLoading, setGroupTypesLoading] = useState(true);
  const [groupTypesSeeding, setGroupTypesSeeding] = useState(false);
  const [newGroupType, setNewGroupType] = useState('');
  const [addingGroupType, setAddingGroupType] = useState(false);
  const [showGroupTypeInput, setShowGroupTypeInput] = useState(false);
  const [editingGroupTypeId, setEditingGroupTypeId] = useState(null);
  const [editGroupTypeName, setEditGroupTypeName] = useState('');

  // Checklist templates
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templatesSeeding, setTemplatesSeeding] = useState(false);
  const [newTask, setNewTask] = useState('');
  const [addingTask, setAddingTask] = useState(false);
  const [showTaskInput, setShowTaskInput] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editTaskName, setEditTaskName] = useState('');

  // Participant fields
  const [participantFields, setParticipantFields] = useState([]);
  const [participantFieldsLoading, setParticipantFieldsLoading] = useState(true);
  const [participantFieldsSeeding, setParticipantFieldsSeeding] = useState(false);
  const [showPFInput, setShowPFInput] = useState(false);
  const [addingPF, setAddingPF] = useState(false);
  const [editingPFId, setEditingPFId] = useState(null);
  const [newPF, setNewPF] = useState({ label: '', type: 'text', required: false, placeholder: '', fullWidth: false });
  const [editPF, setEditPF] = useState({ label: '', type: 'text', required: false, placeholder: '', fullWidth: false });

  // Skeletal itinerary palette items
  const [paletteItems,      setPaletteItems]      = useState([]);
  const [paletteLoading,    setPaletteLoading]    = useState(true);
  const [newPaletteLabel,   setNewPaletteLabel]   = useState('');
  const [newPaletteType,    setNewPaletteType]    = useState('activity');
  const [addingPalette,     setAddingPalette]     = useState(false);
  const [showPaletteInput,  setShowPaletteInput]  = useState(false);

  // Letter templates
  const [letterTemplates,   setLetterTemplates]   = useState([]);
  const [letterLoading,     setLetterLoading]     = useState(true);
  const [letterForm,        setLetterForm]        = useState({ name: '', subject: '', trigger: 'deposit_paid', body: '', enabled: true, noPdf: false, attachQuoteLetter: false, attachmentBody: '' });
  const [editingLetterId,   setEditingLetterId]   = useState(null);
  const [showLetterForm,    setShowLetterForm]    = useState(false);
  const [savingLetter,      setSavingLetter]      = useState(false);

  // Supplier service categories
  const [supplierCats, setSupplierCats] = useState([]);
  const [supplierCatsLoading, setSupplierCatsLoading] = useState(true);
  const [supplierCatsSeeding, setSupplierCatsSeeding] = useState(false);
  const [newSupplierCat, setNewSupplierCat] = useState('');
  const [addingSupplierCat, setAddingSupplierCat] = useState(false);
  const [showSupplierCatInput, setShowSupplierCatInput] = useState(false);
  const [editingSupplierCatId, setEditingSupplierCatId] = useState(null);
  const [editSupplierCatName, setEditSupplierCatName] = useState('');
  const [expandedSupplierCatId, setExpandedSupplierCatId] = useState(null);
  const [scNewField, setScNewField] = useState({ label: '', type: 'text', required: false, placeholder: '' });
  const [scAddingField, setScAddingField] = useState(false);

  // Supplier portal email toggle
  const [supplierEmailsEnabled, setSupplierEmailsEnabled] = useState(false);
  const [savingSupplierToggle, setSavingSupplierToggle] = useState(false);

  // Status update email toggle (off unless turned on)
  const [statusEmailsEnabled, setStatusEmailsEnabled] = useState(false);
  const [savingStatusToggle, setSavingStatusToggle] = useState(false);

  // Your Tour finance contact
  const [ytFinanceName,  setYtFinanceName]  = useState('');
  const [ytFinanceEmail, setYtFinanceEmail] = useState('');
  const [ytFinanceSaving, setYtFinanceSaving] = useState(false);
  const [ytFinanceSaved,  setYtFinanceSaved]  = useState(false);


  // Quote tool — flight price spreadsheet import
  const [qtImportFile, setQtImportFile] = useState(null);
  const [qtImportRecords, setQtImportRecords] = useState(null);
  const [qtImportGroundTable, setQtImportGroundTable] = useState(null);
  const [qtImportWarnings, setQtImportWarnings] = useState([]);
  const [qtImportParsing, setQtImportParsing] = useState(false);
  const [qtImportError, setQtImportError] = useState('');
  const [qtImporting, setQtImporting] = useState(false);
  const [qtImportDone, setQtImportDone] = useState(false);
  const [qtMatrixCount, setQtMatrixCount] = useState(null);

  // Exchange rates
  const [ratesHistory,    setRatesHistory]    = useState([]);
  const [ratesLoading,    setRatesLoading]    = useState(true);
  const [overrideMode,    setOverrideMode]    = useState(null); // 'EUR' | 'USD' | null
  const [overrideValue,   setOverrideValue]   = useState('');
  const [savingOverride,  setSavingOverride]  = useState(false);

  // Navigation
  const [activeSection, setActiveSection] = useState(null);
  const [searchQuery,   setSearchQuery]   = useState('');

  useEffect(() => {
    getDoc(doc(db, 'settings', 'supplierPortal')).then((snap) => {
      if (snap.exists()) setSupplierEmailsEnabled(snap.data()?.supplierEmailsEnabled === true);
    }).catch(console.error);
  }, []);

  useEffect(() => {
    getDoc(doc(db, 'settings', 'emails')).then((snap) => {
      if (snap.exists()) setStatusEmailsEnabled(snap.data()?.statusUpdateEmailsEnabled === true);
    }).catch(console.error);
  }, []);

  async function toggleStatusEmails(value) {
    setSavingStatusToggle(true);
    try {
      await setDoc(doc(db, 'settings', 'emails'), { statusUpdateEmailsEnabled: value }, { merge: true });
      setStatusEmailsEnabled(value);
    } catch (err) {
      console.error(err);
      alert('Failed to save. Please try again.');
    } finally {
      setSavingStatusToggle(false);
    }
  }

  async function toggleSupplierEmails(value) {
    setSavingSupplierToggle(true);
    try {
      await setDoc(doc(db, 'settings', 'supplierPortal'), { supplierEmailsEnabled: value }, { merge: true });
      setSupplierEmailsEnabled(value);
    } catch (err) {
      console.error(err);
      alert('Failed to save. Please try again.');
    } finally {
      setSavingSupplierToggle(false);
    }
  }

  useEffect(() => {
    getDoc(doc(db, 'settings', 'yourTour')).then((snap) => {
      if (snap.exists()) {
        const d = snap.data();
        setYtFinanceName(d.financeContactName || '');
        setYtFinanceEmail(d.financeContactEmail || '');
      }
    }).catch(() => {});
  }, []);

  async function saveYtFinanceContact(e) {
    e.preventDefault();
    setYtFinanceSaving(true);
    try {
      await setDoc(doc(db, 'settings', 'yourTour'), {
        financeContactName:  ytFinanceName.trim(),
        financeContactEmail: ytFinanceEmail.trim(),
      }, { merge: true });
      setYtFinanceSaved(true);
      setTimeout(() => setYtFinanceSaved(false), 2500);
    } finally {
      setYtFinanceSaving(false);
    }
  }

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'priceMatrix'), (snap) => setQtMatrixCount(snap.size));
    return unsub;
  }, []);

  async function handleFlightFileChange(e) {
    const file = e.target.files?.[0];
    setQtImportFile(file || null);
    setQtImportRecords(null);
    setQtImportGroundTable(null);
    setQtImportWarnings([]);
    setQtImportError('');
    setQtImportDone(false);
    if (!file) return;
    setQtImportParsing(true);
    try {
      const { parseFlightSpreadsheet } = await import('../../utils/parseFlightSpreadsheet');
      const buf = await file.arrayBuffer();
      const { records, groundTable, warnings } = parseFlightSpreadsheet(buf);
      if (!records.length) {
        setQtImportError('No price rows were found in this file. Check it matches the expected MST flight price template.');
      } else {
        setQtImportRecords(records);
        setQtImportGroundTable(groundTable);
        setQtImportWarnings(warnings);
      }
    } catch (err) {
      console.error(err);
      // A failed dynamic import means the browser has a stale bundle cached
      // from before the last deploy — the file itself is fine.
      const staleBundle = /loading chunk|dynamically imported module|module script/i.test(err?.message || '');
      setQtImportError(staleBundle
        ? 'The page is running an old version of the site — refresh the page (Cmd+Shift+R on Mac) and try the upload again.'
        : `Could not read this file (${err?.message || 'unknown error'}). Make sure it is the unedited MST flights & hotel prices .xlsx workbook.`);
    } finally {
      setQtImportParsing(false);
    }
  }

  async function confirmFlightImport() {
    if (!qtImportRecords?.length) return;
    if (!window.confirm(`Import ${qtImportRecords.length} flight price rows? This replaces all existing flight price data on the quote tool.`)) return;
    setQtImporting(true);
    try {
      // Clear existing priceMatrix docs first (old import), then write the new set.
      const existing = await getDocs(collection(db, 'priceMatrix'));
      const batchSize = 400;
      const allDocs = existing.docs;
      for (let i = 0; i < allDocs.length; i += batchSize) {
        const b = writeBatch(db);
        allDocs.slice(i, i + batchSize).forEach((d) => b.delete(d.ref));
        await b.commit();
      }
      for (let i = 0; i < qtImportRecords.length; i += batchSize) {
        const b = writeBatch(db);
        qtImportRecords.slice(i, i + batchSize).forEach((rec) => {
          b.set(doc(collection(db, 'priceMatrix')), { ...rec, importedAt: serverTimestamp(), importedBy: currentUser?.email || '' });
        });
        await b.commit();
      }
      if (qtImportGroundTable) {
        await setDoc(doc(db, 'settings', 'quoteTool'), {
          groundPriceTable: qtImportGroundTable,
          updatedAt: serverTimestamp(),
          updatedBy: currentUser?.email || '',
        }, { merge: true });
      }
      setQtImportDone(true);
      setQtImportFile(null);
      setQtImportRecords(null);
      setQtImportGroundTable(null);
    } catch (err) {
      console.error(err);
      setQtImportError('Import failed partway through — check the console and try again.');
    } finally {
      setQtImporting(false);
    }
  }

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'exchangeRates'),
      (snap) => {
        const docs = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => b.id.localeCompare(a.id))
          .slice(0, 8);
        setRatesHistory(docs);
        setRatesLoading(false);
      },
      (err) => {
        console.error('Exchange rates listener error:', err);
        setRatesLoading(false);
      }
    );
    return unsub;
  }, []);

  async function saveRateOverride(e) {
    e.preventDefault();
    const val = parseFloat(overrideValue);
    if (!val || val <= 0 || isNaN(val)) return;
    if (!window.confirm(`Override rate to ${val}? This affects new transactions only — amounts already saved have their rates locked.`)) return;
    setSavingOverride(true);
    try {
      const weekStart = getWeekStart();
      const field = overrideMode === 'EUR' ? 'EUR_GBP' : overrideMode === 'USD' ? 'USD_GBP' : 'GBP_EUR';
      const existing = ratesHistory.find((r) => r.id === weekStart);
      await setDoc(doc(db, 'exchangeRates', weekStart), {
        EUR_GBP: existing?.EUR_GBP ?? 1,
        USD_GBP: existing?.USD_GBP ?? 1,
        GBP_EUR: existing?.GBP_EUR ?? 1,
        pulledAt: existing?.pulledAt ?? serverTimestamp(),
        source: existing?.source ?? 'manual',
        [field]: val,
        manualOverride: true,
        overriddenBy: currentUser.email,
        overriddenAt: serverTimestamp(),
      }, { merge: true });
      setOverrideMode(null);
      setOverrideValue('');
    } finally {
      setSavingOverride(false);
    }
  }

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'tourCategories'), orderBy('order', 'asc')),
      (snap) => {
        setCategories(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      }
    );
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'expenseCategories'), orderBy('order', 'asc')),
      (snap) => {
        setExpenseCats(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setExpenseCatsLoading(false);
      }
    );
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'sports'), orderBy('order', 'asc')),
      (snap) => {
        setSports(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setSportsLoading(false);
      }
    );
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'groupTypes'), orderBy('order', 'asc')),
      (snap) => {
        setGroupTypes(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setGroupTypesLoading(false);
      }
    );
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'checklistTemplates'), orderBy('order', 'asc')),
      (snap) => {
        setTemplates(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setTemplatesLoading(false);
      }
    );
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'participantFields'), orderBy('order', 'asc')),
      (snap) => {
        setParticipantFields(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setParticipantFieldsLoading(false);
      }
    );
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'supplierCategories'), orderBy('order', 'asc')),
      (snap) => {
        setSupplierCats(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setSupplierCatsLoading(false);
      }
    );
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'skeletalPaletteItems'), orderBy('order', 'asc')),
      (snap) => {
        setPaletteItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setPaletteLoading(false);
      }
    );
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'letterTemplates'), orderBy('createdAt', 'asc')),
      (snap) => {
        setLetterTemplates(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLetterLoading(false);
      }
    );
    return unsub;
  }, []);

  async function saveLetterTemplate(e) {
    e.preventDefault();
    if (!letterForm.name.trim() || !letterForm.subject.trim() || !letterForm.body.trim()) return;
    setSavingLetter(true);
    try {
      if (editingLetterId) {
        await updateDoc(doc(db, 'letterTemplates', editingLetterId), {
          ...letterForm,
          updatedAt: serverTimestamp(),
        });
      } else {
        await addDoc(collection(db, 'letterTemplates'), {
          ...letterForm,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
      setShowLetterForm(false);
      setEditingLetterId(null);
      setLetterForm({ name: '', subject: '', trigger: 'deposit_paid', body: '', enabled: true, noPdf: false, attachQuoteLetter: false, attachmentBody: '' });
    } finally {
      setSavingLetter(false);
    }
  }

  function startEditLetter(tmpl) {
    setLetterForm({
      name: tmpl.name, subject: tmpl.subject, trigger: tmpl.trigger, body: tmpl.body, enabled: tmpl.enabled !== false,
      noPdf: tmpl.noPdf === true, attachQuoteLetter: tmpl.attachQuoteLetter === true, attachmentBody: tmpl.attachmentBody || '',
    });
    setEditingLetterId(tmpl.id);
    setShowLetterForm(true);
  }

  async function deleteLetterTemplate(id) {
    if (!window.confirm('Delete this letter template? This cannot be undone.')) return;
    await deleteDoc(doc(db, 'letterTemplates', id));
  }

  async function toggleLetterEnabled(tmpl) {
    await updateDoc(doc(db, 'letterTemplates', tmpl.id), { enabled: !tmpl.enabled });
  }

  async function addPaletteItem(e) {
    e.preventDefault();
    if (!newPaletteLabel.trim()) return;
    setAddingPalette(true);
    try {
      const maxOrder = paletteItems.length > 0 ? Math.max(...paletteItems.map((p) => p.order ?? 0)) : -1;
      await addDoc(collection(db, 'skeletalPaletteItems'), {
        label: newPaletteLabel.trim(),
        type:  newPaletteType,
        order: maxOrder + 1,
        createdAt: serverTimestamp(),
      });
      setNewPaletteLabel('');
      setNewPaletteType('activity');
      setShowPaletteInput(false);
    } finally {
      setAddingPalette(false);
    }
  }

  async function deletePaletteItem(id) {
    if (!window.confirm('Remove this palette item? It will no longer appear in the skeletal itinerary drag tray.')) return;
    await deleteDoc(doc(db, 'skeletalPaletteItems', id));
  }

  async function seedDefaults() {
    if (!window.confirm('This will add all default tour categories. Continue?')) return;
    setSeeding(true);
    try {
      const maxOrder = categories.length > 0
        ? Math.max(...categories.map((c) => c.order ?? 0))
        : -1;
      const existing = new Set(categories.map((c) => c.name.toLowerCase()));
      let offset = maxOrder + 1;
      for (const cat of DEFAULT_TOUR_CATEGORIES) {
        if (!existing.has(cat.name.toLowerCase())) {
          await addDoc(collection(db, 'tourCategories'), {
            name: cat.name,
            order: offset++,
            active: true,
            createdAt: serverTimestamp(),
            createdBy: currentUser.email,
          });
        }
      }
    } finally {
      setSeeding(false);
    }
  }

  async function addCategory(e) {
    e.preventDefault();
    if (!newName.trim()) return;
    setAdding(true);
    try {
      const maxOrder = categories.length > 0
        ? Math.max(...categories.map((c) => c.order ?? 0))
        : -1;
      await addDoc(collection(db, 'tourCategories'), {
        name: newName.trim(),
        order: maxOrder + 1,
        active: true,
        createdAt: serverTimestamp(),
        createdBy: currentUser.email,
      });
      setNewName('');
      setShowInput(false);
    } finally {
      setAdding(false);
    }
  }

  async function toggleActive(cat) {
    await updateDoc(doc(db, 'tourCategories', cat.id), { active: !cat.active });
  }

  async function saveEdit(id) {
    if (!editName.trim()) return;
    await updateDoc(doc(db, 'tourCategories', id), { name: editName.trim() });
    setEditingId(null);
    setEditName('');
  }

  async function deleteCategory(id) {
    if (!window.confirm('Delete this category? It will no longer appear in request forms or price codes.')) return;
    await deleteDoc(doc(db, 'tourCategories', id));
  }

  // Expense category handlers
  async function seedDefaultExpenseCategories() {
    if (!window.confirm('Add all default expense categories? Existing ones will not be duplicated.')) return;
    setExpenseCatsSeeding(true);
    try {
      const maxOrder = expenseCats.length > 0 ? Math.max(...expenseCats.map((c) => c.order ?? 0)) : -1;
      const existing = new Set(expenseCats.map((c) => c.name.toLowerCase()));
      let offset = maxOrder + 1;
      for (const cat of DEFAULT_EXPENSE_CATEGORIES) {
        if (!existing.has(cat.name.toLowerCase())) {
          await addDoc(collection(db, 'expenseCategories'), {
            name: cat.name,
            order: offset++,
            active: true,
            createdAt: serverTimestamp(),
            createdBy: currentUser.email,
          });
        }
      }
    } finally {
      setExpenseCatsSeeding(false);
    }
  }

  async function addExpenseCategory(e) {
    e.preventDefault();
    if (!newExpenseCat.trim()) return;
    setAddingExpenseCat(true);
    try {
      const maxOrder = expenseCats.length > 0 ? Math.max(...expenseCats.map((c) => c.order ?? 0)) : -1;
      await addDoc(collection(db, 'expenseCategories'), {
        name: newExpenseCat.trim(),
        order: maxOrder + 1,
        active: true,
        createdAt: serverTimestamp(),
        createdBy: currentUser.email,
      });
      setNewExpenseCat('');
      setShowExpenseCatInput(false);
    } finally {
      setAddingExpenseCat(false);
    }
  }

  async function toggleExpenseCategoryActive(cat) {
    await updateDoc(doc(db, 'expenseCategories', cat.id), { active: !cat.active });
  }

  async function saveExpenseCategoryEdit(id) {
    if (!editExpenseCatName.trim()) return;
    await updateDoc(doc(db, 'expenseCategories', id), { name: editExpenseCatName.trim() });
    setEditingExpenseCatId(null);
    setEditExpenseCatName('');
  }

  async function deleteExpenseCategory(id) {
    if (!window.confirm('Delete this expense category? Existing expenses keep their category name, but it will no longer appear in the dropdowns.')) return;
    await deleteDoc(doc(db, 'expenseCategories', id));
  }

  // Group type handlers
  async function seedDefaultGroupTypes() {
    if (!window.confirm('Add all default group types? Existing ones will not be duplicated.')) return;
    setGroupTypesSeeding(true);
    try {
      const maxOrder = groupTypes.length > 0 ? Math.max(...groupTypes.map((g) => g.order ?? 0)) : -1;
      const existing = new Set(groupTypes.map((g) => g.name.toLowerCase()));
      let offset = maxOrder + 1;
      for (const name of DEFAULT_GROUP_TYPES) {
        if (!existing.has(name.toLowerCase())) {
          await addDoc(collection(db, 'groupTypes'), {
            name, order: offset++, active: true,
            createdAt: serverTimestamp(), createdBy: currentUser.email,
          });
        }
      }
    } finally { setGroupTypesSeeding(false); }
  }

  async function addGroupType(e) {
    e.preventDefault();
    if (!newGroupType.trim()) return;
    setAddingGroupType(true);
    try {
      const maxOrder = groupTypes.length > 0 ? Math.max(...groupTypes.map((g) => g.order ?? 0)) : -1;
      await addDoc(collection(db, 'groupTypes'), {
        name: newGroupType.trim(), order: maxOrder + 1, active: true,
        createdAt: serverTimestamp(), createdBy: currentUser.email,
      });
      setNewGroupType(''); setShowGroupTypeInput(false);
    } finally { setAddingGroupType(false); }
  }

  async function saveGroupTypeEdit(id) {
    if (!editGroupTypeName.trim()) return;
    await updateDoc(doc(db, 'groupTypes', id), { name: editGroupTypeName.trim() });
    setEditingGroupTypeId(null); setEditGroupTypeName('');
  }

  async function toggleGroupTypeActive(gt) {
    await updateDoc(doc(db, 'groupTypes', gt.id), { active: !gt.active });
  }

  async function deleteGroupType(id) {
    if (!window.confirm('Delete this group type? It will no longer appear in request forms.')) return;
    await deleteDoc(doc(db, 'groupTypes', id));
  }

  // Sports handlers
  async function seedDefaultSports() {
    if (!window.confirm('Add all default sports? Existing ones will not be duplicated.')) return;
    setSportsSeeding(true);
    try {
      const maxOrder = sports.length > 0 ? Math.max(...sports.map((s) => s.order ?? 0)) : -1;
      const existing = new Set(sports.map((s) => s.name.toLowerCase()));
      let offset = maxOrder + 1;
      for (const name of DEFAULT_SPORTS) {
        if (!existing.has(name.toLowerCase())) {
          await addDoc(collection(db, 'sports'), {
            name, order: offset++, active: true,
            createdAt: serverTimestamp(), createdBy: currentUser.email,
          });
        }
      }
    } finally { setSportsSeeding(false); }
  }

  async function addSport(e) {
    e.preventDefault();
    if (!newSport.trim()) return;
    setAddingSport(true);
    try {
      const maxOrder = sports.length > 0 ? Math.max(...sports.map((s) => s.order ?? 0)) : -1;
      await addDoc(collection(db, 'sports'), {
        name: newSport.trim(), order: maxOrder + 1, active: true,
        createdAt: serverTimestamp(), createdBy: currentUser.email,
      });
      setNewSport(''); setShowSportInput(false);
    } finally { setAddingSport(false); }
  }

  async function saveSportEdit(id) {
    if (!editSportName.trim()) return;
    await updateDoc(doc(db, 'sports', id), { name: editSportName.trim() });
    setEditingSportId(null); setEditSportName('');
  }

  async function toggleSportActive(sport) {
    await updateDoc(doc(db, 'sports', sport.id), { active: !sport.active });
  }

  async function deleteSport(id) {
    if (!window.confirm('Delete this sport? It will no longer appear in the request form.')) return;
    await deleteDoc(doc(db, 'sports', id));
  }

  // Checklist template handlers
  async function seedDefaultTasks() {
    if (!window.confirm('Add all default checklist tasks? Existing ones will not be duplicated.')) return;
    setTemplatesSeeding(true);
    try {
      const maxOrder = templates.length > 0 ? Math.max(...templates.map((t) => t.order ?? 0)) : -1;
      const existing = new Set(templates.map((t) => t.name.toLowerCase()));
      let offset = maxOrder + 1;
      for (const name of DEFAULT_CHECKLIST_TASKS) {
        if (!existing.has(name.toLowerCase())) {
          await addDoc(collection(db, 'checklistTemplates'), {
            name, order: offset++, active: true,
            createdAt: serverTimestamp(), createdBy: currentUser.email,
          });
        }
      }
    } finally { setTemplatesSeeding(false); }
  }

  async function addTask(e) {
    e.preventDefault();
    if (!newTask.trim()) return;
    setAddingTask(true);
    try {
      const maxOrder = templates.length > 0 ? Math.max(...templates.map((t) => t.order ?? 0)) : -1;
      await addDoc(collection(db, 'checklistTemplates'), {
        name: newTask.trim(), order: maxOrder + 1, active: true,
        createdAt: serverTimestamp(), createdBy: currentUser.email,
      });
      setNewTask(''); setShowTaskInput(false);
    } finally { setAddingTask(false); }
  }

  async function saveTaskEdit(id) {
    if (!editTaskName.trim()) return;
    await updateDoc(doc(db, 'checklistTemplates', id), { name: editTaskName.trim() });
    setEditingTaskId(null); setEditTaskName('');
  }

  // Soft-delete: set active: false instead of deleting
  async function toggleTaskActive(template) {
    await updateDoc(doc(db, 'checklistTemplates', template.id), { active: !template.active });
  }

  // Participant field handlers
  async function seedDefaultParticipantFields() {
    if (!window.confirm('Add default participant fields? Existing ones will not be duplicated.')) return;
    setParticipantFieldsSeeding(true);
    try {
      const existing = new Set(participantFields.map((f) => f.key));
      for (const field of DEFAULT_PARTICIPANT_FIELDS) {
        if (!existing.has(field.key)) {
          await addDoc(collection(db, 'participantFields'), {
            ...field,
            active: true,
            createdAt: serverTimestamp(),
            createdBy: currentUser.email,
          });
        }
      }
    } finally {
      setParticipantFieldsSeeding(false);
    }
  }

  async function addParticipantField(e) {
    e.preventDefault();
    if (!newPF.label.trim()) return;
    const key = labelToKey(newPF.label);
    if (!key) return;
    setAddingPF(true);
    try {
      const maxOrder = participantFields.length > 0
        ? Math.max(...participantFields.map((f) => f.order ?? 0))
        : -1;
      await addDoc(collection(db, 'participantFields'), {
        label: newPF.label.trim(),
        key,
        type: newPF.type,
        required: newPF.required,
        placeholder: newPF.placeholder.trim(),
        fullWidth: newPF.fullWidth,
        order: maxOrder + 1,
        active: true,
        createdAt: serverTimestamp(),
        createdBy: currentUser.email,
      });
      setNewPF({ label: '', type: 'text', required: false, placeholder: '', fullWidth: false });
      setShowPFInput(false);
    } finally {
      setAddingPF(false);
    }
  }

  function startEditPF(pf) {
    setEditingPFId(pf.id);
    setEditPF({
      label: pf.label,
      type: pf.type,
      required: pf.required || false,
      placeholder: pf.placeholder || '',
      fullWidth: pf.fullWidth || false,
    });
  }

  async function saveEditPF(id) {
    if (!editPF.label.trim()) return;
    await updateDoc(doc(db, 'participantFields', id), {
      label: editPF.label.trim(),
      type: editPF.type,
      required: editPF.required,
      placeholder: editPF.placeholder.trim(),
      fullWidth: editPF.fullWidth,
    });
    setEditingPFId(null);
  }

  async function togglePFActive(pf) {
    await updateDoc(doc(db, 'participantFields', pf.id), { active: !pf.active });
  }

  async function deleteParticipantField(id) {
    if (!window.confirm('Delete this participant field? It will no longer appear in the registration form. Existing participant data is preserved.')) return;
    await deleteDoc(doc(db, 'participantFields', id));
  }

  // Supplier category handlers
  async function seedDefaultSupplierCats() {
    if (!window.confirm('Add all default supplier service categories? Existing ones will not be duplicated.')) return;
    setSupplierCatsSeeding(true);
    try {
      const maxOrder = supplierCats.length > 0 ? Math.max(...supplierCats.map((c) => c.order ?? 0)) : -1;
      const existing = new Set(supplierCats.map((c) => c.name.toLowerCase()));
      let offset = maxOrder + 1;
      for (const cat of DEFAULT_SUPPLIER_CATEGORIES) {
        if (!existing.has(cat.name.toLowerCase())) {
          await addDoc(collection(db, 'supplierCategories'), {
            name: cat.name, order: offset++, active: true,
            createdAt: serverTimestamp(), createdBy: currentUser.email,
          });
        }
      }
    } finally { setSupplierCatsSeeding(false); }
  }

  async function addSupplierCat(e) {
    e.preventDefault();
    if (!newSupplierCat.trim()) return;
    setAddingSupplierCat(true);
    try {
      const maxOrder = supplierCats.length > 0 ? Math.max(...supplierCats.map((c) => c.order ?? 0)) : -1;
      await addDoc(collection(db, 'supplierCategories'), {
        name: newSupplierCat.trim(), order: maxOrder + 1, active: true,
        createdAt: serverTimestamp(), createdBy: currentUser.email,
      });
      setNewSupplierCat(''); setShowSupplierCatInput(false);
    } finally { setAddingSupplierCat(false); }
  }

  async function saveSupplierCatEdit(id) {
    if (!editSupplierCatName.trim()) return;
    await updateDoc(doc(db, 'supplierCategories', id), { name: editSupplierCatName.trim() });
    setEditingSupplierCatId(null); setEditSupplierCatName('');
  }

  async function toggleSupplierCatActive(cat) {
    await updateDoc(doc(db, 'supplierCategories', cat.id), { active: !cat.active });
  }

  async function deleteSupplierCat(id) {
    if (!window.confirm('Delete this supplier category? Suppliers already tagged with it will keep the tag, but it will no longer appear as an option.')) return;
    await deleteDoc(doc(db, 'supplierCategories', id));
  }

  async function addSupplierCatField(e, cat) {
    e.preventDefault();
    if (!scNewField.label.trim()) return;
    const key = labelToKey(scNewField.label);
    if (!key) return;
    const existing = cat.fields || [];
    if (existing.some((f) => f.key === key)) {
      alert(`A field with key "${key}" already exists in this category.`);
      return;
    }
    setScAddingField(true);
    try {
      await updateDoc(doc(db, 'supplierCategories', cat.id), {
        fields: [...existing, {
          key,
          label: scNewField.label.trim(),
          type: scNewField.type,
          required: scNewField.required,
          placeholder: scNewField.placeholder.trim(),
        }],
      });
      setScNewField({ label: '', type: 'text', required: false, placeholder: '' });
    } finally {
      setScAddingField(false);
    }
  }

  async function deleteSupplierCatField(cat, fieldKey) {
    if (!window.confirm('Remove this field from the schema? Existing supplier data using this field is preserved.')) return;
    await updateDoc(doc(db, 'supplierCategories', cat.id), {
      fields: (cat.fields || []).filter((f) => f.key !== fieldKey),
    });
  }

  return (
    <div className="flex flex-col">
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-[20px] font-bold text-[#111827]">Settings</h2>
          <p className="text-[13px] text-[#6B7280] mt-1">Manage tour categories, participant fields, suppliers, communications, and finance.</p>
        </div>
        <input
          type="search"
          placeholder="Search settings…"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="w-56 rounded-[8px] border border-[#E5E7EB] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-accent"
        />
      </div>

      {/* Two-column layout */}
      <div className="flex gap-6 items-start">
        {/* Left rail */}
        <aside className="w-[212px] shrink-0 sticky top-6 bg-white rounded-[12px] border border-[#E5E7EB] p-3">
          <button
            onClick={() => { setActiveSection(null); setSearchQuery(''); }}
            className={`w-full text-left px-3 py-2 rounded-[8px] text-[13px] font-semibold mb-2 transition-colors ${activeSection === null && !searchQuery ? 'bg-accent/10 text-accent' : 'text-[#374151] hover:bg-[#F9FAFB]'}`}
          >
            All Settings
          </button>
          {GROUPS.map(group => (
            <div key={group.key} className="mb-1">
              <p className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-[#9CA3AF]">{group.label}</p>
              {visibleSections.filter(s => s.group === group.key).map(section => (
                <button
                  key={section.key}
                  onClick={() => { setActiveSection(section.key); setSearchQuery(''); }}
                  className={`w-full text-left px-3 py-1.5 rounded-[8px] text-[13px] transition-colors ${activeSection === section.key ? 'bg-accent/10 text-accent font-medium' : 'text-[#374151] hover:bg-[#F9FAFB]'}`}
                >
                  {section.label}
                </button>
              ))}
            </div>
          ))}
        </aside>

        {/* Content area */}
        <main className="flex-1 min-w-0 space-y-6">

          {/* Overview hub — shown when no section selected and no search */}
          {activeSection === null && !searchQuery && (
            <div>
              {GROUPS.map(group => {
                const sections = visibleSections.filter(s => s.group === group.key);
                return (
                  <div key={group.key} className="mb-8">
                    <h3 className="text-[13px] font-semibold uppercase tracking-wide text-[#9CA3AF] mb-3">{group.label}</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {sections.map(section => (
                        <button
                          key={section.key}
                          onClick={() => setActiveSection(section.key)}
                          className="text-left bg-white rounded-[12px] border border-[#E5E7EB] p-4 hover:border-accent hover:shadow-sm transition-all group"
                        >
                          <div className="text-[22px] mb-2">{section.icon}</div>
                          <p className="text-[14px] font-semibold text-[#111827] group-hover:text-accent">{section.label}</p>
                          <p className="text-[12px] text-[#6B7280] mt-0.5">{section.desc}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Search results — shown when search box has text */}
          {searchQuery && (
            <div>
              <p className="text-[13px] text-[#6B7280] mb-4">
                Results for <span className="font-semibold text-[#111827]">"{searchQuery}"</span>
              </p>
              {(() => {
                const q = searchQuery.toLowerCase();
                const results = visibleSections.filter(s =>
                  s.label.toLowerCase().includes(q) || s.desc.toLowerCase().includes(q)
                );
                if (!results.length) {
                  return <p className="text-[13px] text-[#6B7280] py-8 text-center">No settings match your search.</p>;
                }
                return (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {results.map(section => (
                      <button
                        key={section.key}
                        onClick={() => { setActiveSection(section.key); setSearchQuery(''); }}
                        className="text-left bg-white rounded-[12px] border border-[#E5E7EB] p-4 hover:border-accent hover:shadow-sm transition-all group"
                      >
                        <div className="text-[22px] mb-2">{section.icon}</div>
                        <p className="text-[14px] font-semibold text-[#111827] group-hover:text-accent">{section.label}</p>
                        <p className="text-[12px] text-[#6B7280] mt-0.5">{section.desc}</p>
                      </button>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}

      {activeSection === 'finance-contact' && (
        <>
          <div className="flex items-center gap-2 text-[13px] text-[#6B7280] mb-4">
            <button onClick={() => setActiveSection(null)} className="hover:text-accent transition-colors">All Settings</button>
            <span>/</span>
            <span className="font-medium text-[#111827]">Finance Contact</span>
          </div>
          {/* Your Tour Finance Contact */}
          <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6">
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[11px] font-bold px-1.5 py-0.5 rounded-[4px] bg-navy text-white">YT</span>
            <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Your Tour Finance Contact</h3>
          </div>
          <p className="text-[12px] text-[#9CA3AF]">
            When a Your Tour invoice batch is generated, a notification email with the quote and invoices attached
            is sent to this contact so they can produce and send their own invoice to the customer.
          </p>
        </div>
        <form onSubmit={saveYtFinanceContact} className="space-y-3 max-w-md">
          <div>
            <label className="block text-[13px] font-medium text-[#374151] mb-1">Contact name</label>
            <input
              type="text"
              value={ytFinanceName}
              onChange={(e) => setYtFinanceName(e.target.value)}
              placeholder="e.g. Sarah Jones"
              className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
            />
          </div>
          <div>
            <label className="block text-[13px] font-medium text-[#374151] mb-1">Email address</label>
            <input
              type="email"
              value={ytFinanceEmail}
              onChange={(e) => setYtFinanceEmail(e.target.value)}
              placeholder="e.g. finance@your-tour.co.uk"
              className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
            />
          </div>
          <div className="flex items-center gap-3 pt-1">
            <button
              type="submit"
              disabled={ytFinanceSaving || !ytFinanceName.trim() || !ytFinanceEmail.trim()}
              className="px-4 py-2 text-[13px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60"
            >
              {ytFinanceSaving ? 'Saving…' : 'Save'}
            </button>
            {ytFinanceSaved && (
              <span className="text-[13px] text-green-600 font-medium">Saved</span>
            )}
          </div>
        </form>
      </div>
        </>
      )}

      {activeSection === 'quote-tool' && (
        <>
          <div className="flex items-center gap-2 text-[13px] text-[#6B7280] mb-4">
            <button onClick={() => setActiveSection(null)} className="hover:text-accent transition-colors">All Settings</button>
            <span>/</span>
            <span className="font-medium text-[#111827]">Quote Tool</span>
          </div>

          {/* Pricing — now driven by the "Online Quote" price code */}
          <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6 mb-5">
            <div className="mb-3">
              <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Public Instant Quote — Pricing</h3>
            </div>
            <p className="text-[13px] text-[#374151] leading-relaxed">
              Transport, Fixtures, Training Sessions, Stadium Tour and the optional extras (Padel, Yoga, Waterpark, Castle walk) are all priced straight from the <span className="font-semibold">"Online Quote"</span> price code — edit them on the <a href="/staff/price-codes" className="text-pb hover:text-navy underline">Price Codes</a> page, not here.
            </p>
            <p className="text-[13px] text-[#374151] leading-relaxed mt-2">
              Hotel prices come from the fixed 2026-27 rate card (four hotels, date/board/room dependent — in the codebase, not editable here). Flights are never priced automatically either — staff (or the customer, if known) add a real £ fare manually on the quote itself.
            </p>
          </div>

          {/* Flight price import */}
          <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6">
            <div className="mb-5">
              <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Flight Price Data</h3>
              <p className="text-[12px] text-[#9CA3AF] mt-1">
                Upload MST's flight price spreadsheet (.xlsx) to refresh available departure weekends and prices on the public quote page.
                Currently <span className="font-semibold text-[#374151]">{qtMatrixCount == null ? '…' : qtMatrixCount}</span> price rows loaded.
              </p>
            </div>

            <input
              type="file" accept=".xlsx" onChange={handleFlightFileChange}
              className="block text-[13px] mb-3"
            />
            {qtImportFile && <p className="text-[12px] text-[#9CA3AF] mb-3">Selected: {qtImportFile.name}</p>}

            {qtImportParsing && <p className="text-[13px] text-[#9CA3AF]">Reading file…</p>}
            {qtImportError && <p className="text-[13px] text-dr bg-red-light rounded-[8px] px-3 py-2 mb-3">{qtImportError}</p>}

            {qtImportRecords && (
              <div className="mt-3">
                <p className="text-[13px] font-medium text-[#374151] mb-2">
                  Found {qtImportRecords.length} price rows
                  {qtImportGroundTable ? ' plus the accommodation ground price table' : ''}.
                  Preview the first 10 below, then confirm to import.
                </p>
                {qtImportWarnings.length > 0 && (
                  <div className="text-[12px] text-[#92400E] bg-amber-light rounded-[8px] px-3 py-2 mb-3">
                    <p className="font-semibold mb-1">Fixed up while reading the sheet:</p>
                    <ul className="list-disc pl-4 space-y-0.5">
                      {qtImportWarnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  </div>
                )}
                <div className="overflow-x-auto border border-[#E5E7EB] rounded-[8px] mb-3">
                  <table className="w-full text-[12.5px]">
                    <thead>
                      <tr className="bg-surface border-b border-[#E5E7EB]">
                        {['Ratio', 'Region', 'Month', 'Date', 'Nights', 'Airline', 'Ground pp', 'Flights+hotel pp', 'Available'].map((h) => (
                          <th key={h} className="px-3 py-2 text-left font-semibold text-[#6B7280]">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {qtImportRecords.slice(0, 10).map((r, i) => {
                        const cur = r.currency === 'EUR' ? '€' : '£';
                        return (
                          <tr key={i} className="border-b border-[#F1F3F6]">
                            <td className="px-3 py-1.5">{r.ratio || '—'}</td>
                            <td className="px-3 py-1.5">{r.regionLabel}</td>
                            <td className="px-3 py-1.5">{r.month} {r.year}</td>
                            <td className="px-3 py-1.5">{r.dateLabel}</td>
                            <td className="px-3 py-1.5">{r.nights ?? '—'}</td>
                            <td className="px-3 py-1.5">{r.airline}</td>
                            <td className="px-3 py-1.5">{r.groundPricePerPerson != null ? `${cur}${r.groundPricePerPerson}` : '—'}</td>
                            <td className="px-3 py-1.5">
                              {r.selfBooked
                                ? `${cur}${r.selfBookedTotalPerPerson} (total)`
                                : r.packageTotalPerPerson != null
                                  ? `${cur}${r.packageTotalPerPerson}${r.flightPricePerPerson != null ? ` (${cur}${r.flightPricePerPerson} flight)` : ''}`
                                  : '—'}
                            </td>
                            <td className="px-3 py-1.5">{r.selfBooked || r.flightAvailable ? 'Yes' : 'No'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <button
                  onClick={confirmFlightImport}
                  disabled={qtImporting}
                  className="px-4 py-2 text-[13px] font-semibold text-white bg-sg hover:brightness-95 rounded-[8px] transition disabled:opacity-60"
                >
                  {qtImporting ? 'Importing…' : `Confirm import (${qtImportRecords.length} rows)`}
                </button>
              </div>
            )}

            {qtImportDone && (
              <p className="text-[13px] text-green-600 font-medium mt-3">Import complete — the public quote page now uses this data.</p>
            )}
          </div>
        </>
      )}

      {activeSection === 'tour-categories' && (
        <>
          <div className="flex items-center gap-2 text-[13px] text-[#6B7280] mb-4">
            <button onClick={() => setActiveSection(null)} className="hover:text-accent transition-colors">All Settings</button>
            <span>/</span>
            <span className="font-medium text-[#111827]">Tour Categories</span>
          </div>
          {/* Tour Categories */}
          <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Tour Add-on Categories</h3>
            <p className="text-[12px] text-[#9CA3AF] mt-1">
              These options appear on the request form (customer and staff) and in the price code item list.
              Deactivating a category hides it from forms but keeps existing data intact.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {categories.length === 0 && !loading && (
              <button
                onClick={seedDefaults}
                disabled={seeding}
                className="text-[13px] text-pb hover:text-navy font-medium border border-pb/20 hover:border-pb/40 px-4 py-2 rounded-[8px] transition disabled:opacity-60"
              >
                {seeding ? 'Adding defaults…' : 'Load default categories'}
              </button>
            )}
            {!showInput && (
              <button
                onClick={() => setShowInput(true)}
                className="bg-pb hover:bg-navy text-white text-[13px] font-semibold px-4 py-2 rounded-[8px] transition"
              >
                + Add Category
              </button>
            )}
          </div>
        </div>

        {/* Add input */}
        {showInput && (
          <form onSubmit={addCategory} className="flex gap-2 mb-5">
            <input
              type="text" value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="New category name…"
              autoFocus
              className="flex-1 border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
            />
            <button type="submit" disabled={adding || !newName.trim()}
              className="px-[18px] py-[9px] text-[13px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60">
              {adding ? 'Adding…' : 'Add'}
            </button>
            <button type="button" onClick={() => { setShowInput(false); setNewName(''); }}
              className="px-3 py-2 text-[13px] text-[#6B7280] hover:text-[#374151]">
              Cancel
            </button>
          </form>
        )}

        {loading ? (
          <p className="text-[13px] text-[#9CA3AF]">Loading…</p>
        ) : categories.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-[13px] text-[#9CA3AF] mb-2">No categories yet.</p>
            <p className="text-[12px] text-[#9CA3AF]">Use "Load default categories" to get started, or add them manually above.</p>
          </div>
        ) : (
          <div className="border border-[#E5E7EB] rounded-[8px] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-surface border-b border-[#E5E7EB]">
                <tr>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-[#6B7280] uppercase tracking-wide">Category</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-[#6B7280] uppercase tracking-wide">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F3F4F6]">
                {categories.map((cat) => (
                  <tr key={cat.id} className={`hover:bg-surface transition ${!cat.active ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-3">
                      {editingId === cat.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text" value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            autoFocus
                            className="border border-[#E5E7EB] rounded-[8px] px-2 py-1 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                          />
                          <button onClick={() => saveEdit(cat.id)}
                            className="text-[12px] text-white bg-pb hover:bg-navy px-3 py-1 rounded-[6px]">
                            Save
                          </button>
                          <button onClick={() => { setEditingId(null); setEditName(''); }}
                            className="text-[12px] text-[#9CA3AF] hover:text-[#374151]">
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <span className="font-medium text-[#111827]">{cat.name}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        cat.active !== false
                          ? 'bg-green-light text-[#065F46]'
                          : 'bg-[#F3F4F6] text-[#6B7280]'
                      }`}>
                        {cat.active !== false ? 'Active' : 'Hidden'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-3">
                        {editingId !== cat.id && (
                          <button
                            onClick={() => { setEditingId(cat.id); setEditName(cat.name); }}
                            className="text-[12px] text-pb hover:text-navy font-medium"
                          >
                            Rename
                          </button>
                        )}
                        <button
                          onClick={() => toggleActive(cat)}
                          className="text-[12px] text-[#6B7280] hover:text-[#374151] font-medium"
                        >
                          {cat.active !== false ? 'Hide' : 'Show'}
                        </button>
                        <button
                          onClick={() => deleteCategory(cat.id)}
                          className="text-[12px] text-dr/70 hover:text-dr font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
        </>
      )}

      {activeSection === 'expense-categories' && (
        <>
          <div className="flex items-center gap-2 text-[13px] text-[#6B7280] mb-4">
            <button onClick={() => setActiveSection(null)} className="hover:text-accent transition-colors">All Settings</button>
            <span>/</span>
            <span className="font-medium text-[#111827]">Expense Categories</span>
          </div>
          {/* Expense Categories */}
          <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Expense Categories</h3>
            <p className="text-[12px] text-[#9CA3AF] mt-1">
              These options appear in the expense forms (admin and tour leader), the expense filters, and receipt scanning.
              Deactivating a category hides it from forms but keeps existing expenses intact.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {expenseCats.length === 0 && !expenseCatsLoading && (
              <button
                onClick={seedDefaultExpenseCategories}
                disabled={expenseCatsSeeding}
                className="text-[13px] text-pb hover:text-navy font-medium border border-pb/20 hover:border-pb/40 px-4 py-2 rounded-[8px] transition disabled:opacity-60"
              >
                {expenseCatsSeeding ? 'Adding defaults…' : 'Load default categories'}
              </button>
            )}
            {!showExpenseCatInput && (
              <button
                onClick={() => setShowExpenseCatInput(true)}
                className="bg-pb hover:bg-navy text-white text-[13px] font-semibold px-4 py-2 rounded-[8px] transition"
              >
                + Add Category
              </button>
            )}
          </div>
        </div>

        {showExpenseCatInput && (
          <form onSubmit={addExpenseCategory} className="flex gap-2 mb-5">
            <input
              type="text" value={newExpenseCat}
              onChange={(e) => setNewExpenseCat(e.target.value)}
              placeholder="New category name…"
              autoFocus
              className="flex-1 border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
            />
            <button type="submit" disabled={addingExpenseCat || !newExpenseCat.trim()}
              className="px-[18px] py-[9px] text-[13px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60">
              {addingExpenseCat ? 'Adding…' : 'Add'}
            </button>
            <button type="button" onClick={() => { setShowExpenseCatInput(false); setNewExpenseCat(''); }}
              className="px-3 py-2 text-[13px] text-[#6B7280] hover:text-[#374151]">
              Cancel
            </button>
          </form>
        )}

        {expenseCatsLoading ? (
          <p className="text-[13px] text-[#9CA3AF]">Loading…</p>
        ) : expenseCats.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-[13px] text-[#9CA3AF] mb-2">No categories yet — the expense forms are using the built-in defaults.</p>
            <p className="text-[12px] text-[#9CA3AF]">Use "Load default categories" to make them editable, or add them manually above.</p>
          </div>
        ) : (
          <div className="border border-[#E5E7EB] rounded-[8px] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-surface border-b border-[#E5E7EB]">
                <tr>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-[#6B7280] uppercase tracking-wide">Category</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-[#6B7280] uppercase tracking-wide">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F3F4F6]">
                {expenseCats.map((cat) => (
                  <tr key={cat.id} className={`hover:bg-surface transition ${!cat.active ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-3">
                      {editingExpenseCatId === cat.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text" value={editExpenseCatName}
                            onChange={(e) => setEditExpenseCatName(e.target.value)}
                            autoFocus
                            className="border border-[#E5E7EB] rounded-[8px] px-2 py-1 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                          />
                          <button onClick={() => saveExpenseCategoryEdit(cat.id)}
                            className="text-[12px] text-white bg-pb hover:bg-navy px-3 py-1 rounded-[6px]">
                            Save
                          </button>
                          <button onClick={() => { setEditingExpenseCatId(null); setEditExpenseCatName(''); }}
                            className="text-[12px] text-[#9CA3AF] hover:text-[#374151]">
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <span className="font-medium text-[#111827]">{cat.name}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        cat.active !== false
                          ? 'bg-green-light text-[#065F46]'
                          : 'bg-[#F3F4F6] text-[#6B7280]'
                      }`}>
                        {cat.active !== false ? 'Active' : 'Hidden'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-3">
                        {editingExpenseCatId !== cat.id && (
                          <button
                            onClick={() => { setEditingExpenseCatId(cat.id); setEditExpenseCatName(cat.name); }}
                            className="text-[12px] text-pb hover:text-navy font-medium"
                          >
                            Rename
                          </button>
                        )}
                        <button
                          onClick={() => toggleExpenseCategoryActive(cat)}
                          className="text-[12px] text-[#6B7280] hover:text-[#374151] font-medium"
                        >
                          {cat.active !== false ? 'Hide' : 'Show'}
                        </button>
                        <button
                          onClick={() => deleteExpenseCategory(cat.id)}
                          className="text-[12px] text-dr/70 hover:text-dr font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
        </>
      )}

      {activeSection === 'group-types' && (
        <>
          <div className="flex items-center gap-2 text-[13px] text-[#6B7280] mb-4">
            <button onClick={() => setActiveSection(null)} className="hover:text-accent transition-colors">All Settings</button>
            <span>/</span>
            <span className="font-medium text-[#111827]">Group Types</span>
          </div>
          {/* Group Types */}
          <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6 mt-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Group Types</h3>
            <p className="text-[12px] text-[#9CA3AF] mt-1">
              Group types available on the tour request form. Deactivating a type hides it from new requests.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {groupTypes.length === 0 && !groupTypesLoading && (
              <button onClick={seedDefaultGroupTypes} disabled={groupTypesSeeding}
                className="text-[13px] text-pb hover:text-navy font-medium border border-pb/20 hover:border-pb/40 px-4 py-2 rounded-[8px] transition disabled:opacity-60">
                {groupTypesSeeding ? 'Adding…' : 'Load defaults'}
              </button>
            )}
            {!showGroupTypeInput && (
              <button onClick={() => setShowGroupTypeInput(true)}
                className="bg-pb hover:bg-navy text-white text-[13px] font-semibold px-4 py-2 rounded-[8px] transition">
                + Add Group Type
              </button>
            )}
          </div>
        </div>

        {showGroupTypeInput && (
          <form onSubmit={addGroupType} className="flex gap-2 mb-5">
            <input type="text" value={newGroupType} onChange={(e) => setNewGroupType(e.target.value)}
              placeholder="New group type…" autoFocus
              className="flex-1 border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb" />
            <button type="submit" disabled={addingGroupType || !newGroupType.trim()}
              className="px-[18px] py-[9px] text-[13px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60">
              {addingGroupType ? 'Adding…' : 'Add'}
            </button>
            <button type="button" onClick={() => { setShowGroupTypeInput(false); setNewGroupType(''); }}
              className="px-3 py-2 text-[13px] text-[#6B7280] hover:text-[#374151]">Cancel</button>
          </form>
        )}

        {groupTypesLoading ? (
          <p className="text-[13px] text-[#9CA3AF]">Loading…</p>
        ) : groupTypes.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-[13px] text-[#9CA3AF] mb-2">No group types yet.</p>
            <p className="text-[12px] text-[#9CA3AF]">Use "Load defaults" or add manually above.</p>
          </div>
        ) : (
          <div className="border border-[#E5E7EB] rounded-[8px] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-surface border-b border-[#E5E7EB]">
                <tr>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-[#6B7280] uppercase tracking-wide">Group Type</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-[#6B7280] uppercase tracking-wide">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F3F4F6]">
                {groupTypes.map((gt) => (
                  <tr key={gt.id} className={`hover:bg-surface transition ${!gt.active ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-3">
                      {editingGroupTypeId === gt.id ? (
                        <div className="flex items-center gap-2">
                          <input type="text" value={editGroupTypeName}
                            onChange={(e) => setEditGroupTypeName(e.target.value)} autoFocus
                            className="border border-[#E5E7EB] rounded-[8px] px-2 py-1 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb" />
                          <button onClick={() => saveGroupTypeEdit(gt.id)}
                            className="text-[12px] text-white bg-pb hover:bg-navy px-3 py-1 rounded-[6px]">Save</button>
                          <button onClick={() => { setEditingGroupTypeId(null); setEditGroupTypeName(''); }}
                            className="text-[12px] text-[#9CA3AF] hover:text-[#374151]">Cancel</button>
                        </div>
                      ) : (
                        <span className="font-medium text-[#111827]">{gt.name}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        gt.active !== false ? 'bg-green-light text-[#065F46]' : 'bg-[#F3F4F6] text-[#6B7280]'
                      }`}>
                        {gt.active !== false ? 'Active' : 'Hidden'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-3">
                        {editingGroupTypeId !== gt.id && (
                          <button onClick={() => { setEditingGroupTypeId(gt.id); setEditGroupTypeName(gt.name); }}
                            className="text-[12px] text-pb hover:text-navy font-medium">Rename</button>
                        )}
                        <button onClick={() => toggleGroupTypeActive(gt)}
                          className="text-[12px] text-[#6B7280] hover:text-[#374151] font-medium">
                          {gt.active !== false ? 'Hide' : 'Show'}
                        </button>
                        <button onClick={() => deleteGroupType(gt.id)}
                          className="text-[12px] text-dr/70 hover:text-dr font-medium">Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
        </>
      )}

      {activeSection === 'sports' && (
        <>
          <div className="flex items-center gap-2 text-[13px] text-[#6B7280] mb-4">
            <button onClick={() => setActiveSection(null)} className="hover:text-accent transition-colors">All Settings</button>
            <span>/</span>
            <span className="font-medium text-[#111827]">Sports</span>
          </div>
          {/* Sports Management */}
          <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6 mt-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Sports</h3>
            <p className="text-[12px] text-[#9CA3AF] mt-1">
              Sports available on the tour request form. Deactivating a sport hides it from new requests.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {sports.length === 0 && !sportsLoading && (
              <button onClick={seedDefaultSports} disabled={sportsSeeding}
                className="text-[13px] text-pb hover:text-navy font-medium border border-pb/20 hover:border-pb/40 px-4 py-2 rounded-[8px] transition disabled:opacity-60">
                {sportsSeeding ? 'Adding…' : 'Load default sports'}
              </button>
            )}
            {!showSportInput && (
              <button onClick={() => setShowSportInput(true)}
                className="bg-pb hover:bg-navy text-white text-[13px] font-semibold px-4 py-2 rounded-[8px] transition">
                + Add Sport
              </button>
            )}
          </div>
        </div>

        {showSportInput && (
          <form onSubmit={addSport} className="flex gap-2 mb-5">
            <input type="text" value={newSport} onChange={(e) => setNewSport(e.target.value)}
              placeholder="New sport name…" autoFocus
              className="flex-1 border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb" />
            <button type="submit" disabled={addingSport || !newSport.trim()}
              className="px-[18px] py-[9px] text-[13px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60">
              {addingSport ? 'Adding…' : 'Add'}
            </button>
            <button type="button" onClick={() => { setShowSportInput(false); setNewSport(''); }}
              className="px-3 py-2 text-[13px] text-[#6B7280] hover:text-[#374151]">Cancel</button>
          </form>
        )}

        {sportsLoading ? (
          <p className="text-[13px] text-[#9CA3AF]">Loading…</p>
        ) : sports.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-[13px] text-[#9CA3AF] mb-2">No sports yet.</p>
            <p className="text-[12px] text-[#9CA3AF]">Use "Load default sports" or add manually above.</p>
          </div>
        ) : (
          <div className="border border-[#E5E7EB] rounded-[8px] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-surface border-b border-[#E5E7EB]">
                <tr>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-[#6B7280] uppercase tracking-wide">Sport</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-[#6B7280] uppercase tracking-wide">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F3F4F6]">
                {sports.map((sport) => (
                  <tr key={sport.id} className={`hover:bg-surface transition ${!sport.active ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-3">
                      {editingSportId === sport.id ? (
                        <div className="flex items-center gap-2">
                          <input type="text" value={editSportName}
                            onChange={(e) => setEditSportName(e.target.value)} autoFocus
                            className="border border-[#E5E7EB] rounded-[8px] px-2 py-1 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb" />
                          <button onClick={() => saveSportEdit(sport.id)}
                            className="text-[12px] text-white bg-pb hover:bg-navy px-3 py-1 rounded-[6px]">Save</button>
                          <button onClick={() => { setEditingSportId(null); setEditSportName(''); }}
                            className="text-[12px] text-[#9CA3AF] hover:text-[#374151]">Cancel</button>
                        </div>
                      ) : (
                        <span className="font-medium text-[#111827]">{sport.name}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        sport.active !== false ? 'bg-green-light text-[#065F46]' : 'bg-[#F3F4F6] text-[#6B7280]'
                      }`}>
                        {sport.active !== false ? 'Active' : 'Hidden'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-3">
                        {editingSportId !== sport.id && (
                          <button onClick={() => { setEditingSportId(sport.id); setEditSportName(sport.name); }}
                            className="text-[12px] text-pb hover:text-navy font-medium">Rename</button>
                        )}
                        <button onClick={() => toggleSportActive(sport)}
                          className="text-[12px] text-[#6B7280] hover:text-[#374151] font-medium">
                          {sport.active !== false ? 'Hide' : 'Show'}
                        </button>
                        <button onClick={() => deleteSport(sport.id)}
                          className="text-[12px] text-dr/70 hover:text-dr font-medium">Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

        </>
      )}

      {activeSection === 'checklist-templates' && (
        <>
          <div className="flex items-center gap-2 text-[13px] text-[#6B7280] mb-4">
            <button onClick={() => setActiveSection(null)} className="hover:text-accent transition-colors">All Settings</button>
            <span>/</span>
            <span className="font-medium text-[#111827]">Checklist Templates</span>
          </div>
          {/* Ops Checklist Templates */}
          <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6 mt-6">
            <div className="flex items-center justify-between mb-5">
              <div>
            <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Ops Checklist Templates</h3>
            <p className="text-[12px] text-[#9CA3AF] mt-1">
              Default tasks loaded when staff create an ops checklist for a request. Hidden tasks are excluded from new checklists but existing data is preserved.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {templates.length === 0 && !templatesLoading && (
              <button onClick={seedDefaultTasks} disabled={templatesSeeding}
                className="text-[13px] text-pb hover:text-navy font-medium border border-pb/20 hover:border-pb/40 px-4 py-2 rounded-[8px] transition disabled:opacity-60">
                {templatesSeeding ? 'Adding…' : 'Load default tasks'}
              </button>
            )}
            {!showTaskInput && (
              <button onClick={() => setShowTaskInput(true)}
                className="bg-pb hover:bg-navy text-white text-[13px] font-semibold px-4 py-2 rounded-[8px] transition">
                + Add Task
              </button>
            )}
          </div>
        </div>

        {showTaskInput && (
          <form onSubmit={addTask} className="flex gap-2 mb-5">
            <input type="text" value={newTask} onChange={(e) => setNewTask(e.target.value)}
              placeholder="New task description…" autoFocus
              className="flex-1 border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb" />
            <button type="submit" disabled={addingTask || !newTask.trim()}
              className="px-[18px] py-[9px] text-[13px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60">
              {addingTask ? 'Adding…' : 'Add'}
            </button>
            <button type="button" onClick={() => { setShowTaskInput(false); setNewTask(''); }}
              className="px-3 py-2 text-[13px] text-[#6B7280] hover:text-[#374151]">Cancel</button>
          </form>
        )}

        {templatesLoading ? (
          <p className="text-[13px] text-[#9CA3AF]">Loading…</p>
        ) : templates.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-[13px] text-[#9CA3AF] mb-2">No tasks yet.</p>
            <p className="text-[12px] text-[#9CA3AF]">Use "Load default tasks" or add manually above.</p>
          </div>
        ) : (
          <div className="border border-[#E5E7EB] rounded-[8px] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-surface border-b border-[#E5E7EB]">
                <tr>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-[#6B7280] uppercase tracking-wide">Task</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-[#6B7280] uppercase tracking-wide">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F3F4F6]">
                {templates.map((tmpl) => (
                  <tr key={tmpl.id} className={`hover:bg-surface transition ${!tmpl.active ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-3">
                      {editingTaskId === tmpl.id ? (
                        <div className="flex items-center gap-2">
                          <input type="text" value={editTaskName}
                            onChange={(e) => setEditTaskName(e.target.value)} autoFocus
                            className="flex-1 border border-[#E5E7EB] rounded-[8px] px-2 py-1 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb" />
                          <button onClick={() => saveTaskEdit(tmpl.id)}
                            className="text-[12px] text-white bg-pb hover:bg-navy px-3 py-1 rounded-[6px]">Save</button>
                          <button onClick={() => { setEditingTaskId(null); setEditTaskName(''); }}
                            className="text-[12px] text-[#9CA3AF] hover:text-[#374151]">Cancel</button>
                        </div>
                      ) : (
                        <span className="text-[#111827]">{tmpl.name}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        tmpl.active !== false ? 'bg-green-light text-[#065F46]' : 'bg-[#F3F4F6] text-[#6B7280]'
                      }`}>
                        {tmpl.active !== false ? 'Active' : 'Hidden'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-3">
                        {editingTaskId !== tmpl.id && (
                          <button onClick={() => { setEditingTaskId(tmpl.id); setEditTaskName(tmpl.name); }}
                            className="text-[12px] text-pb hover:text-navy font-medium">Edit</button>
                        )}
                        <button onClick={() => toggleTaskActive(tmpl)}
                          className="text-[12px] text-[#6B7280] hover:text-[#374151] font-medium">
                          {tmpl.active !== false ? 'Hide' : 'Show'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

        </>
      )}

      {activeSection === 'supplier-categories' && (
        <>
          <div className="flex items-center gap-2 text-[13px] text-[#6B7280] mb-4">
            <button onClick={() => setActiveSection(null)} className="hover:text-accent transition-colors">All Settings</button>
            <span>/</span>
            <span className="font-medium text-[#111827]">Supplier Categories</span>
          </div>
          {/* Supplier Service Categories */}
          <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6 mt-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Supplier Service Categories</h3>
            <p className="text-[12px] text-[#9CA3AF] mt-1">
              Service types assignable to suppliers. Click <strong>Fields ▼</strong> on any category to define the information fields collected for that supplier type.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {supplierCats.length === 0 && !supplierCatsLoading && (
              <button onClick={seedDefaultSupplierCats} disabled={supplierCatsSeeding}
                className="text-[13px] text-pb hover:text-navy font-medium border border-pb/20 hover:border-pb/40 px-4 py-2 rounded-[8px] transition disabled:opacity-60">
                {supplierCatsSeeding ? 'Adding…' : 'Load defaults'}
              </button>
            )}
            {!showSupplierCatInput && (
              <button onClick={() => setShowSupplierCatInput(true)}
                className="bg-pb hover:bg-navy text-white text-[13px] font-semibold px-4 py-2 rounded-[8px] transition">
                + Add Category
              </button>
            )}
          </div>
        </div>

        {showSupplierCatInput && (
          <form onSubmit={addSupplierCat} className="flex gap-2 mb-5">
            <input type="text" value={newSupplierCat} onChange={(e) => setNewSupplierCat(e.target.value)}
              placeholder="New category name…" autoFocus
              className="flex-1 border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb" />
            <button type="submit" disabled={addingSupplierCat || !newSupplierCat.trim()}
              className="px-[18px] py-[9px] text-[13px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60">
              {addingSupplierCat ? 'Adding…' : 'Add'}
            </button>
            <button type="button" onClick={() => { setShowSupplierCatInput(false); setNewSupplierCat(''); }}
              className="px-3 py-2 text-[13px] text-[#6B7280] hover:text-[#374151]">Cancel</button>
          </form>
        )}

        {supplierCatsLoading ? (
          <p className="text-[13px] text-[#9CA3AF]">Loading…</p>
        ) : supplierCats.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-[13px] text-[#9CA3AF] mb-2">No supplier categories yet.</p>
            <p className="text-[12px] text-[#9CA3AF]">Use "Load defaults" or add them manually above.</p>
          </div>
        ) : (
          <div className="border border-[#E5E7EB] rounded-[8px] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-surface border-b border-[#E5E7EB]">
                <tr>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-[#6B7280] uppercase tracking-wide">Category</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-[#6B7280] uppercase tracking-wide">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F3F4F6]">
                {supplierCats.map((cat) => (
                  <Fragment key={cat.id}>
                    <tr className={`hover:bg-surface transition ${!cat.active ? 'opacity-50' : ''}`}>
                      <td className="px-4 py-3">
                        {editingSupplierCatId === cat.id ? (
                          <div className="flex items-center gap-2">
                            <input type="text" value={editSupplierCatName}
                              onChange={(e) => setEditSupplierCatName(e.target.value)} autoFocus
                              className="border border-[#E5E7EB] rounded-[8px] px-2 py-1 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb" />
                            <button onClick={() => saveSupplierCatEdit(cat.id)}
                              className="text-[12px] text-white bg-pb hover:bg-navy px-3 py-1 rounded-[6px]">Save</button>
                            <button onClick={() => { setEditingSupplierCatId(null); setEditSupplierCatName(''); }}
                              className="text-[12px] text-[#9CA3AF] hover:text-[#374151]">Cancel</button>
                          </div>
                        ) : (
                          <span className="font-medium text-[#111827]">{cat.name}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          cat.active !== false ? 'bg-green-light text-[#065F46]' : 'bg-[#F3F4F6] text-[#6B7280]'
                        }`}>
                          {cat.active !== false ? 'Active' : 'Hidden'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-3">
                          <button
                            onClick={() => {
                              setExpandedSupplierCatId((prev) => prev === cat.id ? null : cat.id);
                              setScNewField({ label: '', type: 'text', required: false, placeholder: '' });
                            }}
                            className={`text-[12px] font-medium transition ${expandedSupplierCatId === cat.id ? 'text-navy' : 'text-pb hover:text-navy'}`}
                          >
                            Fields {(cat.fields?.length ?? 0) > 0 ? `(${cat.fields.length})` : ''} {expandedSupplierCatId === cat.id ? '▲' : '▼'}
                          </button>
                          {editingSupplierCatId !== cat.id && (
                            <button onClick={() => { setEditingSupplierCatId(cat.id); setEditSupplierCatName(cat.name); }}
                              className="text-[12px] text-pb hover:text-navy font-medium">Rename</button>
                          )}
                          <button onClick={() => toggleSupplierCatActive(cat)}
                            className="text-[12px] text-[#6B7280] hover:text-[#374151] font-medium">
                            {cat.active !== false ? 'Hide' : 'Show'}
                          </button>
                          <button onClick={() => deleteSupplierCat(cat.id)}
                            className="text-[12px] text-dr/70 hover:text-dr font-medium">Delete</button>
                        </div>
                      </td>
                    </tr>
                    {expandedSupplierCatId === cat.id && (
                      <tr>
                        <td colSpan={3} className="px-4 pb-4 pt-0 bg-surface/60">
                          <div className="border border-[#E5E7EB] rounded-[8px] p-4 bg-white mt-3">
                            <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-3">
                              Fields for {cat.name} suppliers
                              <span className="ml-1 normal-case font-normal text-[#9CA3AF]">— collected when assigning a {cat.name.toLowerCase()} supplier to a tour</span>
                            </p>

                            {/* Existing fields */}
                            {(cat.fields || []).length === 0 ? (
                              <p className="text-[12px] text-[#9CA3AF] mb-4">No fields defined yet — add some below.</p>
                            ) : (
                              <div className="space-y-2 mb-4">
                                {(cat.fields || []).map((field) => (
                                  <div key={field.key} className="flex items-center gap-3 px-3 py-2 rounded-[6px] border border-[#E5E7EB]">
                                    <span className="text-[13px] font-medium text-[#111827] flex-1">{field.label}</span>
                                    <span className="font-mono text-[11px] text-[#9CA3AF] bg-[#F3F4F6] px-1.5 py-0.5 rounded">{field.key}</span>
                                    <span className="text-[11px] text-[#6B7280]">{PF_TYPE_LABELS[field.type] || field.type}</span>
                                    {field.required && (
                                      <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700">Required</span>
                                    )}
                                    <button type="button" onClick={() => deleteSupplierCatField(cat, field.key)}
                                      className="text-[12px] text-dr/70 hover:text-dr font-medium">Remove</button>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Add field form */}
                            <form onSubmit={(e) => addSupplierCatField(e, cat)} className="flex flex-wrap items-end gap-2 pt-3 border-t border-[#F3F4F6]">
                              <div>
                                <label className="block text-[11px] text-[#6B7280] mb-1">Label <span className="text-dr">*</span></label>
                                <input type="text" value={scNewField.label}
                                  onChange={(e) => setScNewField((p) => ({ ...p, label: e.target.value }))}
                                  placeholder="e.g. Hotel name"
                                  className="border border-[#E5E7EB] rounded-[6px] px-2 py-1.5 text-[13px] focus:outline-none focus:ring-1 focus:ring-pb w-40" />
                              </div>
                              <div>
                                <label className="block text-[11px] text-[#6B7280] mb-1">Type</label>
                                <select value={scNewField.type}
                                  onChange={(e) => setScNewField((p) => ({ ...p, type: e.target.value }))}
                                  className="border border-[#E5E7EB] rounded-[6px] px-2 py-1.5 text-[13px] focus:outline-none focus:ring-1 focus:ring-pb bg-white">
                                  <option value="text">Short text</option>
                                  <option value="textarea">Long text</option>
                                  <option value="date">Date</option>
                                  <option value="number">Number</option>
                                  <option value="tel">Phone</option>
                                  <option value="email">Email</option>
                                </select>
                              </div>
                              <div>
                                <label className="block text-[11px] text-[#6B7280] mb-1">Placeholder</label>
                                <input type="text" value={scNewField.placeholder}
                                  onChange={(e) => setScNewField((p) => ({ ...p, placeholder: e.target.value }))}
                                  placeholder="Optional hint…"
                                  className="border border-[#E5E7EB] rounded-[6px] px-2 py-1.5 text-[13px] focus:outline-none focus:ring-1 focus:ring-pb w-36" />
                              </div>
                              <label className="flex items-center gap-1.5 text-[13px] text-[#374151] cursor-pointer select-none" style={{ paddingBottom: '1px' }}>
                                <input type="checkbox" checked={scNewField.required}
                                  onChange={(e) => setScNewField((p) => ({ ...p, required: e.target.checked }))}
                                  className="rounded border-[#D1D5DB] text-pb" />
                                Required
                              </label>
                              <button type="submit" disabled={scAddingField || !scNewField.label.trim()}
                                className="px-3 py-1.5 text-[13px] font-semibold text-white bg-pb hover:bg-navy rounded-[6px] transition disabled:opacity-60">
                                {scAddingField ? 'Adding…' : '+ Add field'}
                              </button>
                            </form>
                            {scNewField.label.trim() && (
                              <p className="text-[11px] text-[#9CA3AF] mt-1.5">
                                Field key: <span className="font-mono">{labelToKey(scNewField.label)}</span>
                              </p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

        </>
      )}

      {activeSection === 'participant-fields' && (
        <>
          <div className="flex items-center gap-2 text-[13px] text-[#6B7280] mb-4">
            <button onClick={() => setActiveSection(null)} className="hover:text-accent transition-colors">All Settings</button>
            <span>/</span>
            <span className="font-medium text-[#111827]">Participant Fields</span>
          </div>
          {/* Participant Fields */}
          <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6 mt-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Participant Fields</h3>
            <p className="text-[12px] text-[#9CA3AF] mt-1">
              Fields customers must complete when registering participants for a tour. Deactivating a field hides it from the form but preserves existing data.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {participantFields.length === 0 && !participantFieldsLoading && (
              <button
                onClick={seedDefaultParticipantFields}
                disabled={participantFieldsSeeding}
                className="text-[13px] text-pb hover:text-navy font-medium border border-pb/20 hover:border-pb/40 px-4 py-2 rounded-[8px] transition disabled:opacity-60"
              >
                {participantFieldsSeeding ? 'Adding…' : 'Load defaults'}
              </button>
            )}
            {!showPFInput && (
              <button
                onClick={() => setShowPFInput(true)}
                className="bg-pb hover:bg-navy text-white text-[13px] font-semibold px-4 py-2 rounded-[8px] transition"
              >
                + Add Field
              </button>
            )}
          </div>
        </div>

        {/* Add field form */}
        {showPFInput && (
          <form onSubmit={addParticipantField} className="mb-5 p-4 bg-surface rounded-[10px] border border-[#E5E7EB]">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-[12px] font-medium text-[#6B7280] mb-1">
                  Field label <span className="text-dr">*</span>
                </label>
                <input
                  type="text"
                  value={newPF.label}
                  onChange={(e) => setNewPF((p) => ({ ...p, label: e.target.value }))}
                  placeholder="e.g. Medical conditions"
                  autoFocus
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
                />
                {newPF.label.trim() && (
                  <p className="text-[11px] text-[#9CA3AF] mt-1">
                    Field key: <span className="font-mono">{labelToKey(newPF.label)}</span>
                  </p>
                )}
              </div>
              <div>
                <label className="block text-[12px] font-medium text-[#6B7280] mb-1">Input type</label>
                <select
                  value={newPF.type}
                  onChange={(e) => setNewPF((p) => ({ ...p, type: e.target.value }))}
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb bg-white"
                >
                  <option value="text">Short text</option>
                  <option value="textarea">Long text</option>
                  <option value="date">Date</option>
                  <option value="tel">Phone number</option>
                  <option value="email">Email</option>
                  <option value="number">Number</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-[12px] font-medium text-[#6B7280] mb-1">Placeholder text (optional)</label>
                <input
                  type="text"
                  value={newPF.placeholder}
                  onChange={(e) => setNewPF((p) => ({ ...p, placeholder: e.target.value }))}
                  placeholder="Hint shown inside the input…"
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
                />
              </div>
            </div>
            <div className="flex items-center gap-6 mb-4">
              <label className="flex items-center gap-2 text-[13px] text-[#374151] cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={newPF.required}
                  onChange={(e) => setNewPF((p) => ({ ...p, required: e.target.checked }))}
                  className="rounded border-[#D1D5DB] text-pb focus:ring-pb"
                />
                Required
              </label>
              <label className="flex items-center gap-2 text-[13px] text-[#374151] cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={newPF.fullWidth}
                  onChange={(e) => setNewPF((p) => ({ ...p, fullWidth: e.target.checked }))}
                  className="rounded border-[#D1D5DB] text-pb focus:ring-pb"
                />
                Full width (spans both columns)
              </label>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={addingPF || !newPF.label.trim()}
                className="px-[18px] py-[9px] text-[13px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60"
              >
                {addingPF ? 'Adding…' : 'Add field'}
              </button>
              <button
                type="button"
                onClick={() => { setShowPFInput(false); setNewPF({ label: '', type: 'text', required: false, placeholder: '', fullWidth: false }); }}
                className="px-3 py-2 text-[13px] text-[#6B7280] hover:text-[#374151]"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {participantFieldsLoading ? (
          <p className="text-[13px] text-[#9CA3AF]">Loading…</p>
        ) : participantFields.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-[13px] text-[#9CA3AF] mb-2">No participant fields configured.</p>
            <p className="text-[12px] text-[#9CA3AF]">Use "Load defaults" to add the standard fields, or add custom ones above.</p>
          </div>
        ) : (
          <div className="border border-[#E5E7EB] rounded-[8px] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-surface border-b border-[#E5E7EB]">
                <tr>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-[#6B7280] uppercase tracking-wide">Label</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-[#6B7280] uppercase tracking-wide">Key</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-[#6B7280] uppercase tracking-wide">Type</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-[#6B7280] uppercase tracking-wide">Required</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-[#6B7280] uppercase tracking-wide">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F3F4F6]">
                {participantFields.map((pf) => (
                  <Fragment key={pf.id}>
                    <tr className={`hover:bg-surface transition ${!pf.active ? 'opacity-50' : ''}`}>
                      <td className="px-4 py-3 font-medium text-[#111827]">{pf.label}</td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-[12px] text-[#6B7280] bg-[#F3F4F6] px-1.5 py-0.5 rounded">{pf.key}</span>
                      </td>
                      <td className="px-4 py-3 text-[#374151]">{PF_TYPE_LABELS[pf.type] || pf.type}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          pf.required ? 'bg-amber-50 text-amber-700' : 'bg-[#F3F4F6] text-[#6B7280]'
                        }`}>
                          {pf.required ? 'Required' : 'Optional'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          pf.active !== false ? 'bg-green-light text-[#065F46]' : 'bg-[#F3F4F6] text-[#6B7280]'
                        }`}>
                          {pf.active !== false ? 'Active' : 'Hidden'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-3">
                          {editingPFId !== pf.id && (
                            <button
                              onClick={() => startEditPF(pf)}
                              className="text-[12px] text-pb hover:text-navy font-medium"
                            >
                              Edit
                            </button>
                          )}
                          <button
                            onClick={() => togglePFActive(pf)}
                            className="text-[12px] text-[#6B7280] hover:text-[#374151] font-medium"
                          >
                            {pf.active !== false ? 'Hide' : 'Show'}
                          </button>
                          <button
                            onClick={() => deleteParticipantField(pf.id)}
                            className="text-[12px] text-dr/70 hover:text-dr font-medium"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                    {editingPFId === pf.id && (
                      <tr key={`${pf.id}-edit`}>
                        <td colSpan={6} className="px-4 pb-4 pt-1">
                          <div className="p-4 bg-surface rounded-[10px] border border-[#E5E7EB]">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                              <div>
                                <label className="block text-[12px] font-medium text-[#6B7280] mb-1">
                                  Field label <span className="text-dr">*</span>
                                </label>
                                <input
                                  type="text"
                                  value={editPF.label}
                                  onChange={(e) => setEditPF((p) => ({ ...p, label: e.target.value }))}
                                  autoFocus
                                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
                                />
                                <p className="text-[11px] text-[#9CA3AF] mt-1">
                                  Field key: <span className="font-mono">{pf.key}</span> (cannot be changed)
                                </p>
                              </div>
                              <div>
                                <label className="block text-[12px] font-medium text-[#6B7280] mb-1">Input type</label>
                                <select
                                  value={editPF.type}
                                  onChange={(e) => setEditPF((p) => ({ ...p, type: e.target.value }))}
                                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb bg-white"
                                >
                                  <option value="text">Short text</option>
                                  <option value="textarea">Long text</option>
                                  <option value="date">Date</option>
                                  <option value="tel">Phone number</option>
                                  <option value="email">Email</option>
                                  <option value="number">Number</option>
                                </select>
                              </div>
                              <div className="sm:col-span-2">
                                <label className="block text-[12px] font-medium text-[#6B7280] mb-1">Placeholder text (optional)</label>
                                <input
                                  type="text"
                                  value={editPF.placeholder}
                                  onChange={(e) => setEditPF((p) => ({ ...p, placeholder: e.target.value }))}
                                  placeholder="Hint shown inside the input…"
                                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
                                />
                              </div>
                            </div>
                            <div className="flex items-center gap-6 mb-4">
                              <label className="flex items-center gap-2 text-[13px] text-[#374151] cursor-pointer select-none">
                                <input
                                  type="checkbox"
                                  checked={editPF.required}
                                  onChange={(e) => setEditPF((p) => ({ ...p, required: e.target.checked }))}
                                  className="rounded border-[#D1D5DB] text-pb focus:ring-pb"
                                />
                                Required
                              </label>
                              <label className="flex items-center gap-2 text-[13px] text-[#374151] cursor-pointer select-none">
                                <input
                                  type="checkbox"
                                  checked={editPF.fullWidth}
                                  onChange={(e) => setEditPF((p) => ({ ...p, fullWidth: e.target.checked }))}
                                  className="rounded border-[#D1D5DB] text-pb focus:ring-pb"
                                />
                                Full width (spans both columns)
                              </label>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => saveEditPF(pf.id)}
                                disabled={!editPF.label.trim()}
                                className="px-[18px] py-[9px] text-[13px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => setEditingPFId(null)}
                                className="px-3 py-2 text-[13px] text-[#6B7280] hover:text-[#374151]"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
        </>
      )}

      {activeSection === 'itinerary-palette' && (
        <>
          <div className="flex items-center gap-2 text-[13px] text-[#6B7280] mb-4">
            <button onClick={() => setActiveSection(null)} className="hover:text-accent transition-colors">All Settings</button>
            <span>/</span>
            <span className="font-medium text-[#111827]">Itinerary Palette</span>
          </div>
          {/* Skeletal Itinerary Palette Items */}
          <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Skeletal Itinerary — Palette Items</h3>
          {!showPaletteInput && (
            <button
              onClick={() => setShowPaletteInput(true)}
              className="text-[12px] font-semibold text-pb hover:text-navy"
            >
              + Add item
            </button>
          )}
        </div>
        <p className="text-[12px] text-[#9CA3AF] mb-4">
          Activities shown in the drag-and-drop tray when building a skeletal itinerary. Flight items (Arrival/Departure) are always available and cannot be removed.
        </p>

        {showPaletteInput && (
          <form onSubmit={addPaletteItem} className="flex flex-wrap items-end gap-3 p-4 bg-surface border border-[#E5E7EB] rounded-[10px] mb-4">
            <div className="flex-1 min-w-[160px]">
              <label className="block text-[11px] font-medium text-[#6B7280] mb-1">Label</label>
              <input
                type="text"
                value={newPaletteLabel}
                onChange={(e) => setNewPaletteLabel(e.target.value)}
                placeholder="e.g. Yoga Session"
                className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-[#6B7280] mb-1">Type</label>
              <select
                value={newPaletteType}
                onChange={(e) => setNewPaletteType(e.target.value)}
                className="border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
              >
                <option value="activity">Activity (blue)</option>
                <option value="flight">Flight (green)</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={addingPalette || !newPaletteLabel.trim()}
                className="px-[18px] py-[9px] text-[13px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60"
              >
                {addingPalette ? 'Adding…' : 'Add'}
              </button>
              <button
                type="button"
                onClick={() => { setShowPaletteInput(false); setNewPaletteLabel(''); setNewPaletteType('activity'); }}
                className="px-3 py-2 text-[13px] text-[#6B7280] hover:text-[#374151]"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {paletteLoading ? (
          <p className="text-[13px] text-[#9CA3AF]">Loading…</p>
        ) : paletteItems.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-[13px] text-[#9CA3AF] mb-1">No custom palette items yet.</p>
            <p className="text-[12px] text-[#9CA3AF]">The default activity set will be used until you add items here.</p>
          </div>
        ) : (
          <div className="border border-[#E5E7EB] rounded-[8px] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-surface border-b border-[#E5E7EB]">
                <tr>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-[#6B7280] uppercase tracking-wide">Label</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-[#6B7280] uppercase tracking-wide">Type</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F3F4F6]">
                {paletteItems.map((item) => (
                  <tr key={item.id} className="hover:bg-surface transition">
                    <td className="px-4 py-3 font-medium text-[#111827]">{item.label}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${item.type === 'flight' ? 'bg-green-light text-[#065F46]' : 'bg-blue-light text-pb'}`}>
                        {item.type === 'flight' ? 'Flight' : 'Activity'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => deletePaletteItem(item.id)}
                        className="text-[12px] text-dr/70 hover:text-dr font-medium"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

        </>
      )}

      {activeSection === 'letter-templates' && (
        <>
          <div className="flex items-center gap-2 text-[13px] text-[#6B7280] mb-4">
            <button onClick={() => setActiveSection(null)} className="hover:text-accent transition-colors">All Settings</button>
            <span>/</span>
            <span className="font-medium text-[#111827]">Letter Templates</span>
          </div>
          {/* ── Letter Templates ─────────────────────────────────────────────── */}
          <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-[15px] font-bold text-[#111827]">Letter Templates</h2>
            <p className="text-[13px] text-[#6B7280] mt-0.5">
              Create letters that are automatically emailed (with PDF attached) at set trigger points, or sent manually from a tour.
            </p>
          </div>
          {!showLetterForm && (
            <button
              onClick={() => { setShowLetterForm(true); setEditingLetterId(null); setLetterForm({ name: '', subject: '', trigger: 'deposit_paid', body: '', enabled: true }); }}
              className="px-[18px] py-[9px] text-[13px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition flex-shrink-0"
            >
              + New Letter
            </button>
          )}
        </div>

        {/* Merge tag reference */}
        <div className="mb-5 p-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-[8px]">
          <p className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-2">Available merge tags</p>
          <div className="flex flex-wrap gap-2">
            {MERGE_TAGS.map(({ tag, desc }) => (
              <span key={tag} title={desc} className="text-[11px] font-mono bg-white border border-[#E5E7EB] rounded px-1.5 py-0.5 text-pb cursor-default">
                {tag}
              </span>
            ))}
          </div>
          <p className="text-[11px] text-[#9CA3AF] mt-2">Hover a tag to see what it inserts. Paste tags directly into the subject or body.</p>
        </div>

        {/* Create / edit form */}
        {showLetterForm && (
          <form onSubmit={saveLetterTemplate} className="border border-[#E5E7EB] rounded-[10px] p-5 mb-5 space-y-4">
            <p className="text-[13px] font-semibold text-[#111827]">{editingLetterId ? 'Edit letter template' : 'New letter template'}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Template name <span className="text-dr">*</span></label>
                <input
                  type="text" value={letterForm.name} onChange={(e) => setLetterForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Welcome letter" required
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Send trigger</label>
                <select
                  value={letterForm.trigger} onChange={(e) => setLetterForm((p) => ({ ...p, trigger: e.target.value }))}
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                >
                  {LETTER_TRIGGERS.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Email subject <span className="text-dr">*</span></label>
              <input
                type="text" value={letterForm.subject} onChange={(e) => setLetterForm((p) => ({ ...p, subject: e.target.value }))}
                placeholder="e.g. Welcome to your {{destination}} tour, {{contact_name}}!" required
                className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
              />
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={letterForm.noPdf} onChange={(e) => setLetterForm((p) => ({ ...p, noPdf: e.target.checked }))}
                  className="accent-pb" />
                <span className="text-[13px] text-[#374151]">Send as a full email, no PDF attached</span>
              </label>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">
                {letterForm.noPdf ? 'Email body' : 'Letter body'} <span className="text-dr">*</span>
              </label>
              <p className="text-[11px] text-[#9CA3AF] mb-1.5">
                {letterForm.noPdf
                  ? 'This is the full email the contact sees. Use a blank line between paragraphs. Merge tags will be replaced with tour data.'
                  : 'Paste your letter content here. Use a blank line between paragraphs. Merge tags will be replaced with tour data. The email shows a short preview; the full text becomes the attached PDF.'}
              </p>
              <textarea
                rows={14} value={letterForm.body} onChange={(e) => setLetterForm((p) => ({ ...p, body: e.target.value }))}
                placeholder={"Dear {{contact_name}},\n\nWe are delighted to confirm your upcoming tour to {{destination}}...\n\nKind regards,\nMST Sports Tours"}
                required
                className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] font-mono focus:outline-none focus:ring-2 focus:ring-pb resize-y"
              />
            </div>
            {letterForm.noPdf && (
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={letterForm.attachQuoteLetter} onChange={(e) => setLetterForm((p) => ({ ...p, attachQuoteLetter: e.target.checked }))}
                    className="accent-pb" />
                  <span className="text-[13px] text-[#374151]">Attach the tour's actual quote letter (pulled live from its Quote tab)</span>
                </label>
              </div>
            )}
            {letterForm.noPdf && !letterForm.attachQuoteLetter && (
              <div>
                <label className="block text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Also attach a PDF with different text (optional)</label>
                <p className="text-[11px] text-[#9CA3AF] mb-1.5">Leave blank to send the email above with no attachment. Set this to attach a PDF alongside it with its own text — e.g. a formal invitation letter attached to a shorter contact-facing email.</p>
                <textarea
                  rows={8} value={letterForm.attachmentBody} onChange={(e) => setLetterForm((p) => ({ ...p, attachmentBody: e.target.value }))}
                  placeholder={"Dear Parents/Guardians,\n\nWe are delighted to confirm..."}
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] font-mono focus:outline-none focus:ring-2 focus:ring-pb resize-y"
                />
              </div>
            )}
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={letterForm.enabled} onChange={(e) => setLetterForm((p) => ({ ...p, enabled: e.target.checked }))}
                  className="accent-pb" />
                <span className="text-[13px] text-[#374151]">Enabled (will auto-send on trigger)</span>
              </label>
            </div>
            <div className="flex gap-2">
              <button type="submit" disabled={savingLetter}
                className="px-[18px] py-[9px] text-[13px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60">
                {savingLetter ? 'Saving…' : (editingLetterId ? 'Save changes' : 'Create template')}
              </button>
              <button type="button" onClick={() => { setShowLetterForm(false); setEditingLetterId(null); }}
                className="px-4 py-2 text-[13px] text-[#6B7280] hover:text-[#374151]">
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Templates list */}
        {letterLoading ? (
          <p className="text-[13px] text-[#9CA3AF]">Loading…</p>
        ) : letterTemplates.length === 0 && !showLetterForm ? (
          <div className="text-center py-8 border border-dashed border-[#E5E7EB] rounded-[10px]">
            <p className="text-[13px] text-[#9CA3AF]">No letter templates yet.</p>
            <p className="text-[12px] text-[#9CA3AF] mt-1">Create one to start sending automated or manual letters to tour contacts.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {letterTemplates.map((tmpl) => {
              const triggerLabel = LETTER_TRIGGERS.find((t) => t.value === tmpl.trigger)?.label || tmpl.trigger;
              return (
                <div key={tmpl.id} className="border border-[#E5E7EB] rounded-[10px] p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <p className="text-[13px] font-semibold text-[#111827]">{tmpl.name}</p>
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${tmpl.enabled !== false ? 'bg-green-light text-sg' : 'bg-[#F3F4F6] text-[#9CA3AF]'}`}>
                          {tmpl.enabled !== false ? 'Enabled' : 'Disabled'}
                        </span>
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-light text-pb">
                          {triggerLabel}
                        </span>
                      </div>
                      <p className="text-[12px] text-[#6B7280] truncate">{tmpl.subject}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button onClick={() => toggleLetterEnabled(tmpl)}
                        className="text-[12px] text-[#6B7280] hover:text-[#374151] font-medium transition">
                        {tmpl.enabled !== false ? 'Disable' : 'Enable'}
                      </button>
                      <button onClick={() => startEditLetter(tmpl)}
                        className="text-[12px] text-pb hover:text-navy font-medium transition">
                        Edit
                      </button>
                      <button onClick={() => deleteLetterTemplate(tmpl.id)}
                        className="text-[12px] text-dr/70 hover:text-dr font-medium transition">
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

        </>
      )}

      {activeSection === 'exchange-rates' && (
        <>
          <div className="flex items-center gap-2 text-[13px] text-[#6B7280] mb-4">
            <button onClick={() => setActiveSection(null)} className="hover:text-accent transition-colors">All Settings</button>
            <span>/</span>
            <span className="font-medium text-[#111827]">Exchange Rates</span>
          </div>
          {/* Exchange Rates */}
          <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6 mt-6">
        <div className="mb-5">
          <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1">Exchange Rates</h3>
          <p className="text-[12px] text-[#9CA3AF]">
            Rates are pulled automatically every Monday at 07:00 UTC from the European Central Bank via Frankfurter.
            All new expenses, invoices, and quotes are assigned that week's rate and a GBP equivalent is stored permanently on each document.
            Overriding a rate here only affects new transactions — amounts already saved have their rates locked.
          </p>
        </div>

        {ratesLoading ? (
          <p className="text-[13px] text-[#9CA3AF]">Loading…</p>
        ) : ratesHistory.length === 0 ? (
          <div className="text-center py-8 border border-dashed border-[#E5E7EB] rounded-[10px]">
            <p className="text-[13px] text-[#9CA3AF]">No rates available yet.</p>
            <p className="text-[12px] text-[#9CA3AF] mt-1">Rates will be pulled automatically next Monday at 07:00 UTC.</p>
          </div>
        ) : (() => {
          const currentWeekStart = getWeekStart();
          const current = ratesHistory[0];
          const pulledMs = current?.pulledAt?.toMillis ? current.pulledAt.toMillis() : null;
          const isStale = !pulledMs || (Date.now() - pulledMs) > 8 * 24 * 60 * 60 * 1000;
          const fmtDate = (id) => {
            if (!id) return '—';
            const [y, m, d] = id.split('-');
            return `${d}/${m}/${y}`;
          };
          const fmtRate = (r) => r != null ? r.toFixed(6) : '—';

          return (
            <div className="space-y-4">
              {isStale && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-[8px]">
                  <span className="text-amber-500 text-[14px] mt-0.5">⚠</span>
                  <p className="text-[12px] text-amber-700">
                    Rates are from w/c {fmtDate(current.id)} — they may be outdated. The scheduled pull runs every Monday at 07:00 UTC.
                    You can set a manual override below if needed.
                  </p>
                </div>
              )}

              {/* Current week rates */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[{ label: 'EUR → GBP', field: 'EUR_GBP', code: 'EUR', symbol: '€' }, { label: 'USD → GBP', field: 'USD_GBP', code: 'USD', symbol: '$' }, { label: 'GBP → EUR', field: 'GBP_EUR', code: 'GBP_EUR', symbol: '£' }].map(({ label, field, code }) => (
                  <div key={code} className="border border-[#E5E7EB] rounded-[10px] p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[12px] font-semibold text-[#374151]">{label}</span>
                      {current.manualOverride && current.id === currentWeekStart && (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">Manual override</span>
                      )}
                    </div>
                    <p className="text-[22px] font-bold text-[#111827] mb-1">{fmtRate(current[field])}</p>
                    <p className="text-[11px] text-[#9CA3AF]">
                      w/c {fmtDate(current.id)}{current.source ? ` · ${current.source}` : ''}
                      {current.overriddenBy ? ` · overridden by ${current.overriddenBy}` : ''}
                    </p>

                    {overrideMode === code ? (
                      <form onSubmit={saveRateOverride} className="mt-3 flex items-center gap-2">
                        <input
                          type="number"
                          step="0.000001"
                          min="0.000001"
                          value={overrideValue}
                          onChange={(e) => setOverrideValue(e.target.value)}
                          placeholder={fmtRate(current[field])}
                          className="w-32 border border-[#E5E7EB] rounded-[6px] px-2 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                          autoFocus
                        />
                        <button
                          type="submit"
                          disabled={savingOverride || !overrideValue}
                          className="px-3 py-1.5 text-[12px] font-semibold text-white bg-pb hover:bg-navy rounded-[6px] transition disabled:opacity-60"
                        >
                          {savingOverride ? 'Saving…' : 'Save'}
                        </button>
                        <button type="button" onClick={() => { setOverrideMode(null); setOverrideValue(''); }}
                          className="px-3 py-1.5 text-[12px] text-[#6B7280] hover:text-[#374151]">
                          Cancel
                        </button>
                      </form>
                    ) : (
                      <button
                        onClick={() => { setOverrideMode(code); setOverrideValue(fmtRate(current[field])); }}
                        className="mt-3 text-[12px] text-pb hover:text-navy font-medium transition"
                      >
                        Override rate
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* History table */}
              {ratesHistory.length > 1 && (
                <div>
                  <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-2">Recent history</p>
                  <div className="border border-[#E5E7EB] rounded-[10px] overflow-hidden">
                    <table className="w-full text-[12px]">
                      <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                        <tr>
                          <th className="text-left px-4 py-2 text-[#6B7280] font-semibold">Week commencing</th>
                          <th className="text-right px-4 py-2 text-[#6B7280] font-semibold">EUR → GBP</th>
                          <th className="text-right px-4 py-2 text-[#6B7280] font-semibold">USD → GBP</th>
                          <th className="text-right px-4 py-2 text-[#6B7280] font-semibold">GBP → EUR</th>
                          <th className="text-right px-4 py-2 text-[#6B7280] font-semibold">Source</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#E5E7EB]">
                        {ratesHistory.map((r) => (
                          <tr key={r.id} className={r.id === currentWeekStart ? 'bg-blue-50' : ''}>
                            <td className="px-4 py-2 text-[#374151] font-medium">
                              {fmtDate(r.id)}
                              {r.id === currentWeekStart && <span className="ml-2 text-[10px] font-semibold text-pb">Current</span>}
                            </td>
                            <td className="px-4 py-2 text-right text-[#374151]">{fmtRate(r.EUR_GBP)}</td>
                            <td className="px-4 py-2 text-right text-[#374151]">{fmtRate(r.USD_GBP)}</td>
                            <td className="px-4 py-2 text-right text-[#374151]">{r.GBP_EUR ? fmtRate(r.GBP_EUR) : '—'}</td>
                            <td className="px-4 py-2 text-right text-[#9CA3AF]">
                              {r.manualOverride ? 'Manual' : (r.source || '—')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          );
        })()}
      </div>

        </>
      )}

      {activeSection === 'status-pipeline' && isAdmin && (
        <>
          <div className="flex items-center gap-2 text-[13px] text-[#6B7280] mb-4">
            <button onClick={() => setActiveSection(null)} className="hover:text-accent transition-colors">All Settings</button>
            <span>/</span>
            <span className="font-medium text-[#111827]">Status Pipeline</span>
          </div>
          <StatusPipelineSettings />
        </>
      )}

      {activeSection === 'contract-signing' && isAdmin && (
        <>
          <div className="flex items-center gap-2 text-[13px] text-[#6B7280] mb-4">
            <button onClick={() => setActiveSection(null)} className="hover:text-accent transition-colors">All Settings</button>
            <span>/</span>
            <span className="font-medium text-[#111827]">Contract Signing</span>
          </div>
          <ContractSettings />
        </>
      )}

      {activeSection === 'status-emails' && (
        <>
          <div className="flex items-center gap-2 text-[13px] text-[#6B7280] mb-4">
            <button onClick={() => setActiveSection(null)} className="hover:text-accent transition-colors">All Settings</button>
            <span>/</span>
            <span className="font-medium text-[#111827]">Status Update Emails</span>
          </div>
          <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6 mt-6">
            <div className="mb-4">
              <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1">Status Update Emails</h3>
              <p className="text-[12px] text-[#9CA3AF]">
                Customer emails saying their tour's status changed or their quote was updated, and staff alerts that only report good news
                (quote declined, supplier booking confirmed, skeletal itinerary approved). Emails with letters or PDFs, payment confirmation,
                and anything that needs action (new requests, messages, declines, change requests, approval requests) always send.
              </p>
            </div>
            <div className="flex items-center justify-between gap-4 bg-[#F9FAFB] rounded-[10px] border border-[#E5E7EB] px-5 py-4">
              <div>
                <p className="text-[13px] font-bold text-[#111827]">Status update emails</p>
                <p className="text-[12px] text-[#9CA3AF] mt-0.5">
                  {statusEmailsEnabled ? 'On — status update emails are being sent' : 'Off — status update emails are not sent'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => toggleStatusEmails(!statusEmailsEnabled)}
                disabled={savingStatusToggle}
                className={`relative inline-flex h-[28px] w-[52px] flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none disabled:opacity-60 ${
                  statusEmailsEnabled ? 'bg-sg' : 'bg-[#D1D5DB]'
                }`}
                aria-pressed={statusEmailsEnabled}
              >
                <span className={`pointer-events-none inline-block h-[22px] w-[22px] transform rounded-full bg-white shadow-sm transition duration-200 ${statusEmailsEnabled ? 'translate-x-6' : 'translate-x-0'}`} />
              </button>
            </div>
          </div>
        </>
      )}

      {activeSection === 'supplier-emails' && (
        <>
          <div className="flex items-center gap-2 text-[13px] text-[#6B7280] mb-4">
            <button onClick={() => setActiveSection(null)} className="hover:text-accent transition-colors">All Settings</button>
            <span>/</span>
            <span className="font-medium text-[#111827]">Supplier Emails</span>
          </div>
          {/* ── Supplier Portal Settings ── */}
          <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6 mt-6">
        <div className="mb-4">
          <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1">Supplier Portal Emails</h3>
          <p className="text-[12px] text-[#9CA3AF]">
            Turn this on when the supplier portal is ready for live use. While off, no emails are sent to suppliers
            (booking notifications, itinerary approvals, food orders) — staff and customer emails are unaffected.
          </p>
        </div>
        <div className="flex items-center justify-between gap-4 bg-[#F9FAFB] rounded-[10px] border border-[#E5E7EB] px-5 py-4">
          <div>
            <p className="text-[13px] font-bold text-[#111827]">Supplier portal emails</p>
            <p className="text-[12px] text-[#9CA3AF] mt-0.5">
              {supplierEmailsEnabled
                ? 'Live — suppliers are receiving email notifications'
                : 'Off — no emails are being sent to suppliers'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => toggleSupplierEmails(!supplierEmailsEnabled)}
            disabled={savingSupplierToggle}
            className={`relative inline-flex h-[28px] w-[52px] flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none disabled:opacity-60 ${
              supplierEmailsEnabled ? 'bg-sg' : 'bg-[#D1D5DB]'
            }`}
            aria-pressed={supplierEmailsEnabled}
          >
            <span className={`pointer-events-none inline-block h-[22px] w-[22px] transform rounded-full bg-white shadow-sm transition duration-200 ${supplierEmailsEnabled ? 'translate-x-6' : 'translate-x-0'}`} />
          </button>
        </div>
        {supplierEmailsEnabled && (
          <p className="mt-3 text-[12px] text-sg font-semibold">Supplier emails are live.</p>
        )}
      </div>
        </>
      )}

        </main>
      </div>
    </div>
  );
}
